/**
 * GameEngine - Core Turn State Machine & Match Lifecycle
 * Encapsulates deal structures, cut evaluation, dealer selection,
 * His Heels scoring, circular pegging turn advancement, "Go" settlement,
 * and match game-over conditions across all game formats.
 */

import { GameState } from './GameState.js';
import { Card } from '../models/Card.js';
import { Hand } from '../models/Hand.js';
import { CribbageAI } from '../ai/CribbageAI.js';
import { ObstacleGeometry } from '../board/ObstacleGeometry.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { CelebrationFX } from '../ui/CelebrationFX.js';
import { SeriesTracker } from '../series/SeriesTracker.js';
import { TournamentManager } from '../series/TournamentManager.js';
import { ReactionManager } from '../ui/ReactionManager.js';
import { HandScoreTracker } from '../stats/HandScoreTracker.js';
import { GameReplayManager } from './GameReplayManager.js';
import { CribbageScorer, isSpadeCard } from '../scoring/CribbageScorer.js';
import { ModalManager } from '../ui/ModalManager.js';
import { CanadianDoublesModal } from '../variants/CanadianDoublesModal.js';
import { AvatarManager } from '../ui/AvatarManager.js';

export class GameEngine {
  /**
   * Computes the deal and discard configuration based on player count and deal style.
   * @param {number} [playerCount=2]
   * @param {string} [dealStyle='standard'] 'standard' | 'super'
   * @returns {{ dealCount: number, discardCount: number, keepCount: number, extraCribDealt: number }}
   */
  static getDealStructure(playerCount = 2, dealStyle = 'standard', options = {}) {
    const settings = typeof dealStyle === 'object' ? dealStyle : (typeof options === 'object' ? options : {});
    const style = typeof dealStyle === 'string' ? dealStyle : (settings.deal || 'standard');
    const count = Math.max(2, Math.min(8, parseInt(playerCount, 10) || 2));
    if (count === 4 && settings.optCanadianDoubles) {
      return { dealCount: 10, discardCount: 2, keepCount: 4, extraCribDealt: 0 };
    }
    if (count === 2 && settings.opt7CardCribbage) {
      return { dealCount: 7, discardCount: 2, keepCount: 5, extraCribDealt: 1 };
    }
    if (style === 'super' && count === 2) {
      return { dealCount: 9, discardCount: 3, keepCount: 6, extraCribDealt: 0 };
    }
    if (count === 2) {
      return { dealCount: 6, discardCount: 2, keepCount: 4, extraCribDealt: 0 };
    }
    if (count === 3) {
      return { dealCount: 5, discardCount: 1, keepCount: 4, extraCribDealt: 1 };
    }
    // 4 or more players
    return { dealCount: 5, discardCount: 1, keepCount: 4, extraCribDealt: 0 };
  }

  /**
   * Evaluates cut cards between players to determine initial dealer.
   * Lowest rank card cuts dealer (Ace is 1, lowest).
   * @param {Array<{ seatId: string, card: Object|Card }>} cuts
   * @returns {{ winnerSeatId: string|null, isTie: boolean }}
   */
  static resolveCutWinner(cuts) {
    if (!Array.isArray(cuts) || cuts.length === 0) {
      return { winnerSeatId: null, isTie: false };
    }
    let lowestRank = 999;
    let winner = null;
    let tie = false;

    for (const cut of cuts) {
      const card = Card.from(cut.card);
      const r = card ? card.rank : 13;
      if (r < lowestRank) {
        lowestRank = r;
        winner = cut.seatId;
        tie = false;
      } else if (r === lowestRank) {
        tie = true;
      }
    }

    return {
      winnerSeatId: tie ? null : winner,
      isTie: tie
    };
  }

  /**
   * Checks if turning the starter card awards dealer "His Heels" (Two for his heels).
   * A cut Jack awards 2 points to the dealer.
   * @param {Object|Card} starterCard
   * @returns {number} 2 if Jack, 0 otherwise
   */
  static checkHisHeels(starterCard) {
    if (!starterCard) return 0;
    const card = Card.from(starterCard);
    return card && card.rank === 11 ? 2 : 0;
  }

  /**
   * Finds the next active player who has a card that fits under currentCount.
   * Traverses seats circularly starting immediately after fromSeat.
   * @param {string} fromSeat Current playing seat ID
   * @param {number} count Current pegging count (0 to 31)
   * @param {Array<string>} activeSeats Ordered circular array of active seat IDs
   * @param {Object<string, Array>} seatHands Map of seatId -> cards
   * @returns {string|null} The next seat ID with a legal play, or null if nobody can play
   */
  static findNextPlayerWithPlay(fromSeat, count, activeSeats, seatHands) {
    let seats = activeSeats;
    if (!Array.isArray(seats) || seats.length === 0) {
      if (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function') {
        seats = window.getActiveSeatIds();
      } else {
        seats = ['p1', 'p2'];
      }
    }
    let hands = seatHands;
    if (!hands) {
      hands = {};
      for (const s of seats) {
        if (typeof window !== 'undefined' && typeof window.getSeatHand === 'function') {
          hands[s] = window.getSeatHand(s);
        } else if (typeof window !== 'undefined' && window.state && window.state.hands) {
          hands[s] = window.state.hands[s] || [];
        }
      }
    }
    const cleanFrom = fromSeat === 'you' ? 'p1' : (fromSeat === 'cpu' ? 'p2' : fromSeat);
    const fromIdx = seats.indexOf(cleanFrom);
    const startIdx = fromIdx !== -1 ? (fromIdx + 1) % seats.length : 0;

    for (let i = 0; i < seats.length; i++) {
      const idx = (startIdx + i) % seats.length;
      const sId = seats[idx];
      const cards = hands ? (hands[sId] || []) : [];
      const hand = new Hand(cards);
      if (hand.hasPlayableCard(count)) {
        return sId;
      }
    }
    return null;
  }

  /**
   * Evaluates who leads the next pegging series after a 31 or Go reset.
   * The lead passes to the next player with cards remaining in hand.
   * @param {string} whoJustPlayed Seat ID of player who claimed the Go/31
   * @param {Array<string>} activeSeats Ordered active seats
   * @param {Object<string, Array>} seatHands Map of seatId -> remaining cards
   * @returns {string|null} Next lead seat ID or null if all hands empty
   */
  static getNextLeadPlayer(whoJustPlayed, activeSeats, seatHands) {
    let seats = activeSeats;
    if (!Array.isArray(seats) || seats.length === 0) {
      if (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function') {
        seats = window.getActiveSeatIds();
      } else {
        seats = ['p1', 'p2'];
      }
    }
    let hands = seatHands;
    if (!hands) {
      hands = {};
      for (const s of seats) {
        if (typeof window !== 'undefined' && typeof window.getSeatHand === 'function') {
          hands[s] = window.getSeatHand(s);
        } else if (typeof window !== 'undefined' && window.state && window.state.hands) {
          hands[s] = window.state.hands[s] || [];
        }
      }
    }
    const cleanFrom = whoJustPlayed === 'you' ? 'p1' : (whoJustPlayed === 'cpu' ? 'p2' : whoJustPlayed);
    const fromIdx = seats.indexOf(cleanFrom);
    const startIdx = fromIdx !== -1 ? (fromIdx + 1) % seats.length : 0;

    for (let i = 0; i < seats.length; i++) {
      const idx = (startIdx + i) % seats.length;
      const sId = seats[idx];
      const cards = hands ? (hands[sId] || []) : [];
      if (cards.length > 0) {
        return sId;
      }
    }
    return null;
  }

  /**
   * Computes the point value for a Go or 31 pegging hit.
   * @param {number} currentCount
   * @param {boolean} [opt31For5=false] Whether 31's for 5 variant is active
   * @returns {{ points: number, text: string }}
   */
  static calculateGoPoints(currentCount, opt31For5 = false) {
    if (currentCount === 31) {
      const pts = opt31For5 ? 5 : 2;
      return { points: pts, text: 'Thirty-one!' };
    }
    return { points: 1, text: 'GO' };
  }

  /**
   * Checks whether match target score has been reached and resolves the winner.
   * Handles both standard Highball and reverse Lowball rules.
   * @param {Object<string, number>} scores Map of track/seat -> score
   * @param {number} [target=121]
   * @param {string} [goal='high'] 'high' | 'low'
   * @param {Array<string>} [tracks=['p1', 'p2']]
   * @returns {{ over: boolean, winner: string|null }}
   */
  static checkMatchOver(scores = {}, target = 121, goal = 'high', tracks = ['p1', 'p2']) {
    const t = parseInt(target, 10) || 121;
    let over = false;
    let winner = null;

    if (goal === 'high') {
      for (const key of tracks) {
        const sc = scores[key] || 0;
        if (sc >= t) {
          over = true;
          if (!winner || sc > (scores[winner] || 0)) {
            winner = key;
          }
        }
      }
    } else {
      // Lowball: First player to reach or exceed target LOSES
      for (const key of tracks) {
        const sc = scores[key] || 0;
        if (sc >= t) {
          over = true;
          const others = tracks.filter(k => k !== key);
          others.sort((a, b) => (scores[a] || 0) - (scores[b] || 0));
          winner = others[0] || tracks[0];
          break;
        }
      }
    }

    return { over, winner };
  }

  /**
   * Applies a score delta to a player/team, resolving Stink Hole rules,
   * obstacle hazards (Jail, Bar, Timeout, Detour, Roundabout), No Passing Lane
   * restrictions, Stop Signs, Jump zones, and match winning checks.
   *
   * @param {string} who Player or team identifier
   * @param {number} n Points to add (or subtract)
   * @param {string} why Description of points
   * @param {Object} [context={}] Match context options
   * @returns {Object} Result summary describing points scored, hazard progression, and game status
   */
  static applyScoreDelta(who, n, why = '', context = {}) {
    if (!n || n === 0) {
      return { scored: 0, rawPoints: 0, key: null, noop: true };
    }

    const state = context.state || {};
    const settings = context.settings || {};
    const target = parseInt(settings.target, 10) || 121;
    const activeTracks = context.activeTracks || ['p1', 'p2'];

    const key = (typeof context.scoreKey === 'function')
      ? context.scoreKey(who)
      : (who === 'you' ? 'p1' : (who === 'cpu' ? 'p2' : who));

    if (!state.scores) state.scores = {};
    if (!state.prev) state.prev = {};
    if (state.scores[key] === undefined) state.scores[key] = 0;
    if (state.prev[key] === undefined) state.prev[key] = 0;

    if (!context.isSignAction) {
      if (!state.lastScoreDelta) state.lastScoreDelta = {};
      state.lastScoreDelta[key] = n;
    }

    // Stink Hole: Must peg exactly 1 to win if sitting on target - 1
    if (settings.optStinkHole && state.scores[key] === target - 1) {
      if (n !== 1 && n > 0) {
        return {
          scored: 0,
          rawPoints: n,
          key,
          stinkHoleBlocked: true,
          hazard: null
        };
      }
    }

    // Jail: If jailed and scoring positive points, forfeit points and use 1 turn
    if (settings.optJail && state.jailed && state.jailed[key] && state.jailed[key].turnsRemaining > 0) {
      if (n > 0) {
        state.jailed[key].turnsRemaining--;
        state.jailed[key].step = (state.jailed[key].step || 0) + 1;
        const left = state.jailed[key].turnsRemaining;
        const used = 3 - left;
        const trapHole = state.jailed[key].jailHole;
        const freed = left === 0;
        if (freed) {
          delete state.jailed[key];
        }
        return {
          scored: 0,
          rawPoints: n,
          key,
          hazard: 'jail',
          penaltyUsed: { left, used, freed, trapHole }
        };
      }
    }

    // The Bar: If at bar and scoring positive points, forfeit points and use 1 turn
    if (settings.optBar && state.atBar && state.atBar[key] && state.atBar[key].turnsRemaining > 0) {
      if (n > 0) {
        state.atBar[key].turnsRemaining--;
        state.atBar[key].step = (state.atBar[key].step || 0) + 1;
        const left = state.atBar[key].turnsRemaining;
        const used = 3 - left;
        const trapHole = state.atBar[key].barHole;
        const freed = left === 0;
        if (freed) {
          delete state.atBar[key];
        }
        return {
          scored: 0,
          rawPoints: n,
          key,
          hazard: 'bar',
          penaltyUsed: { left, used, freed, trapHole }
        };
      }
    }

    // Timeout: If in timeout and scoring positive points, forfeit points and use 1 turn
    if (settings.optTimeout && state.inTimeout && state.inTimeout[key] && state.inTimeout[key].turnsRemaining > 0) {
      if (n > 0) {
        state.inTimeout[key].turnsRemaining--;
        state.inTimeout[key].step = (state.inTimeout[key].step || 0) + 1;
        const left = state.inTimeout[key].turnsRemaining;
        const used = 3 - left;
        const trapHole = state.inTimeout[key].timeoutHole;
        const freed = left === 0;
        if (freed) {
          delete state.inTimeout[key];
        }
        return {
          scored: 0,
          rawPoints: n,
          key,
          hazard: 'timeout',
          penaltyUsed: { left, used, freed, trapHole }
        };
      }
    }

    // Detour: If in detour and scoring positive points, advance along 7-hole detour loop
    if (settings.optDetour && state.inDetour && state.inDetour[key] && n > 0) {
      const dInfo = state.inDetour[key];
      const newProgress = (dInfo.progress || 0) + n;
      if (newProgress <= 7) {
        dInfo.progress = newProgress;
        return {
          scored: 0,
          rawPoints: n,
          key,
          hazard: 'detour',
          detourProgress: { progress: newProgress, cleared: false, detourHole: dInfo.detourHole }
        };
      } else {
        const overflow = newProgress - 7;
        const targetHole = Math.min(target, dInfo.detourHole + overflow);
        delete state.inDetour[key];
        state.prev[key] = state.scores[key];
        state.scores[key] = targetHole;
        GameEngine._syncLegacyScoreAliases(state, key);
        return {
          scored: targetHole - state.prev[key],
          rawPoints: n,
          key,
          hazard: 'detour',
          detourProgress: { progress: newProgress, cleared: true, targetHole, detourHole: dInfo.detourHole }
        };
      }
    }

    // Round About: If in roundabout and scoring positive points, advance along 7-hole loop
    if (settings.optRoundAbout && state.inRoundAbout && state.inRoundAbout[key] && n > 0) {
      const rInfo = state.inRoundAbout[key];
      const newProgress = (rInfo.progress || 0) + n;
      if (newProgress <= 7) {
        rInfo.progress = newProgress;
        return {
          scored: 0,
          rawPoints: n,
          key,
          hazard: 'roundabout',
          roundAboutProgress: { progress: newProgress, cleared: false, roundAboutHole: rInfo.roundAboutHole }
        };
      } else {
        const overflow = newProgress - 7;
        const targetHole = Math.min(target, rInfo.roundAboutHole + overflow);
        delete state.inRoundAbout[key];
        state.prev[key] = state.scores[key];
        state.scores[key] = targetHole;
        GameEngine._syncLegacyScoreAliases(state, key);
        return {
          scored: targetHole - state.prev[key],
          rawPoints: n,
          key,
          hazard: 'roundabout',
          roundAboutProgress: { progress: newProgress, cleared: true, targetHole, roundAboutHole: rInfo.roundAboutHole }
        };
      }
    }

    let ptsToAdd = n;
    let laneBlocked = false;
    let laneBlocker = null;

    if (n > 0) {
      const currentScore = state.scores[key];
      const targetScore = currentScore + n;
      const passCheckFn = context.getNoPassing || (ObstacleGeometry && ObstacleGeometry.getNoPassingLaneMaxScore);
      if (typeof passCheckFn === 'function') {
        const passCheck = passCheckFn(who, currentScore, targetScore, state, settings, activeTracks);
        if (passCheck && passCheck.blocked) {
          laneBlocked = true;
          laneBlocker = passCheck.blockingPlayer;
          ptsToAdd = Math.max(0, passCheck.allowedScore - currentScore);
        }
      }
    }

    let stopSignHit = null;
    if (n > 0 && settings.optStopSign) {
      const getStopsFn = context.getStopSigns || (ObstacleGeometry && ObstacleGeometry.getStopSignConfigs);
      if (typeof getStopsFn === 'function') {
        const stopSigns = getStopsFn(settings);
        if (Array.isArray(stopSigns)) {
          const currentScore = state.scores[key];
          const targetHole = currentScore + ptsToAdd;
          const hitStops = stopSigns.filter(s => s && s.hole > currentScore && s.hole <= targetHole);
          if (hitStops.length > 0) {
            hitStops.sort((a, b) => a.hole - b.hole);
            const firstStop = hitStops[0];
            const maxAllowed = firstStop.hole - currentScore;
            if (maxAllowed < ptsToAdd) {
              const lost = ptsToAdd - Math.max(0, maxAllowed);
              ptsToAdd = Math.max(0, maxAllowed);
              stopSignHit = { hole: firstStop.hole, ptsLost: lost };
            }
          }
        }
      }
    }

    state.prev[key] = state.scores[key];
    state.scores[key] = Math.max(0, state.scores[key] + ptsToAdd);

    // Jump Option Check
    let jumpTriggered = null;
    if (settings.optJump) {
      const getJumpFn = context.getJump || (ObstacleGeometry && ObstacleGeometry.getJumpForHole);
      if (typeof getJumpFn === 'function') {
        const jump = getJumpFn(state.scores[key], settings);
        if (jump && jump.dest !== state.scores[key]) {
          const landedHole = state.scores[key];
          let targetDest = jump.dest;
          let finalDest = targetDest;
          if (jump.isForward) {
            const passCheckFn = context.getNoPassing || (ObstacleGeometry && ObstacleGeometry.getNoPassingLaneMaxScore);
            if (typeof passCheckFn === 'function') {
              const jumpCheck = passCheckFn(who, landedHole, targetDest, state, settings, activeTracks);
              if (jumpCheck && jumpCheck.allowedScore !== undefined) {
                finalDest = jumpCheck.allowedScore;
              }
            }
          }
          if (finalDest !== landedHole) {
            state.scores[key] = Math.max(0, Math.min(target, finalDest));
            const diff = finalDest - landedHole;
            jumpTriggered = {
              from: landedHole,
              to: finalDest,
              diff,
              isForward: Boolean(jump.isForward)
            };
          }
        }
      }
    }

    // Landing on traps check
    let newTrapLanded = null;
    if (settings.optJail) {
      const getJailFn = context.getJail || (ObstacleGeometry && ObstacleGeometry.getJailForHole);
      if (typeof getJailFn === 'function') {
        const jail = getJailFn(state.scores[key], settings);
        if (jail && (!state.jailed || !state.jailed[key])) {
          if (!state.jailed) state.jailed = {};
          state.jailed[key] = { jailHole: state.scores[key], turnsRemaining: 3, step: 0 };
          newTrapLanded = 'jail';
        }
      }
    }

    if (!newTrapLanded && settings.optBar) {
      const getBarFn = context.getBar || (ObstacleGeometry && ObstacleGeometry.getBarForHole);
      if (typeof getBarFn === 'function') {
        const bar = getBarFn(state.scores[key], settings);
        if (bar && (!state.atBar || !state.atBar[key])) {
          if (!state.atBar) state.atBar = {};
          state.atBar[key] = { barHole: state.scores[key], turnsRemaining: 3, step: 0 };
          newTrapLanded = 'bar';
        }
      }
    }

    if (!newTrapLanded && settings.optTimeout) {
      const getTimeoutFn = context.getTimeout || (ObstacleGeometry && ObstacleGeometry.getTimeoutForHole);
      if (typeof getTimeoutFn === 'function') {
        const timeout = getTimeoutFn(state.scores[key], settings);
        if (timeout && (!state.inTimeout || !state.inTimeout[key])) {
          if (!state.inTimeout) state.inTimeout = {};
          state.inTimeout[key] = { timeoutHole: state.scores[key], turnsRemaining: 3, step: 0 };
          newTrapLanded = 'timeout';
        }
      }
    }

    if (!newTrapLanded && settings.optDetour) {
      const getDetourFn = context.getDetour || (ObstacleGeometry && ObstacleGeometry.getDetourForHole);
      if (typeof getDetourFn === 'function') {
        const detour = getDetourFn(state.scores[key], settings);
        if (detour && (!state.inDetour || !state.inDetour[key])) {
          if (!state.inDetour) state.inDetour = {};
          state.inDetour[key] = { detourHole: state.scores[key], progress: 0 };
          newTrapLanded = 'detour';
        }
      }
    }

    if (!newTrapLanded && settings.optRoundAbout) {
      const getRoundAboutFn = context.getRoundAbout || (ObstacleGeometry && ObstacleGeometry.getRoundAboutForHole);
      if (typeof getRoundAboutFn === 'function') {
        const roundAbout = getRoundAboutFn(state.scores[key], settings);
        if (roundAbout && (!state.inRoundAbout || !state.inRoundAbout[key])) {
          if (!state.inRoundAbout) state.inRoundAbout = {};
          state.inRoundAbout[key] = { roundAboutHole: state.scores[key], progress: 0 };
          newTrapLanded = 'roundabout';
        }
      }
    }

    let signBlockLanded = null;
    if (settings.optSigns && !context.isSignAction && !state._inSignAction) {
      const getSignsFn = context.getSignBlock || (ObstacleGeometry && ObstacleGeometry.getSignBlockForHole);
      if (typeof getSignsFn === 'function') {
        const signBlock = getSignsFn(state.scores[key], settings);
        if (signBlock) {
          signBlockLanded = signBlock;
        }
      }
    }

    let diceLanded = null;
    if (settings.optDice && !context.isSignAction && !state._inDiceAction && !state._inSignAction) {
      const getDiceFn = context.getDice || (ObstacleGeometry && ObstacleGeometry.getDiceForHole);
      if (typeof getDiceFn === 'function') {
        const diceHit = getDiceFn(state.scores[key], settings);
        if (diceHit) {
          diceLanded = diceHit;
        }
      }
    }

    let wheelLanded = null;
    if ((settings.optWheel || settings.optSpinningWheel) && !context.isSignAction && !state._inWheelAction && !state._inSignAction) {
      const getWheelFn = context.getWheel || (ObstacleGeometry && ObstacleGeometry.getWheelForHole);
      if (typeof getWheelFn === 'function') {
        const wheelHit = getWheelFn(state.scores[key], settings);
        if (wheelHit) {
          wheelLanded = wheelHit;
        }
      }
    }

    // Sync legacy score aliases
    GameEngine._syncLegacyScoreAliases(state, key);

    const oldS = state.prev[key];
    const newS = state.scores[key];
    const marks = [241, 361, 481].includes(target)
      ? [30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390, 420, 450, 481].filter(m => m < target).concat([target])
      : [30, 60, 90, target];
    const passedMarks = marks.filter(m => oldS < m && newS >= m);
    const gameHoleReached = (newS >= target && oldS < target);

    let chutesTriggered = false;
    if (settings.optChutesAndLadders) {
      chutesTriggered = GameEngine.checkChutesAndLadders(state, settings, {
        ...context,
        getActiveTracks: () => activeTracks
      });
      if (!chutesTriggered) {
        state.chutesAndLaddersTriggered = false;
        state.chutesAndLaddersTiedScore = null;
      }
    }

    const gameOver = chutesTriggered ? { over: false, winner: null } : GameEngine.checkMatchOver(state.scores, target, settings.goal || 'high', activeTracks);

    return {
      scored: ptsToAdd,
      rawPoints: n,
      key,
      hazard: null,
      laneBlocked,
      laneBlocker,
      stopSignHit,
      jumpTriggered,
      newTrapLanded,
      signBlockLanded,
      diceLanded,
      wheelLanded,
      passedMarks,
      gameHoleReached,
      chutesTriggered,
      gameOver
    };
  }

  /**
   * Advances the dealer button to the next active player circularly.
   * @param {string} currentDealer Current dealer seat ID (e.g. 'p1', 'p2')
   * @param {Array<string>} [activeSeats=['p1', 'p2']] Ordered active seats
   * @returns {string} Next dealer seat ID
   */
  static advanceDealer(currentDealer, activeSeats = ['p1', 'p2']) {
    if (!Array.isArray(activeSeats) || activeSeats.length === 0) return 'p1';
    const cur = currentDealer === 'you' ? 'p1' : (currentDealer === 'cpu' ? 'p2' : currentDealer);
    const idx = activeSeats.indexOf(cur);
    if (idx === -1) return activeSeats[0];
    return activeSeats[(idx + 1) % activeSeats.length];
  }

  /**
   * Builds the sequence of show acts for the show / counting phase.
   * @param {string} dealer Current dealer seat ID
   * @param {Array<string>} [activeSeats=['p1', 'p2']]
   * @param {boolean} [hasCrib=true]
   * @returns {Array<string>} List of act keys (e.g. ['pone', 'dealer', 'crib'])
   */
  static getShowQueue(dealer, activeSeats = ['p1', 'p2'], hasCrib = true) {
    if (!Array.isArray(activeSeats) || activeSeats.length <= 2) {
      const q = ['pone', 'dealer'];
      if (hasCrib) q.push('crib');
      return q;
    }
    const d = dealer === 'you' ? 'p1' : (dealer === 'cpu' ? 'p2' : dealer);
    const dIdx = activeSeats.indexOf(d);
    const startIdx = dIdx !== -1 ? (dIdx + 1) % activeSeats.length : 0;
    const q = [];
    for (let i = 0; i < activeSeats.length; i++) {
      const sId = activeSeats[(startIdx + i) % activeSeats.length];
      q.push(sId);
    }
    if (hasCrib) q.push('crib');
    return q;
  }

  /**
   * Advances the show act index and determines the next act descriptor.
   * @param {number} currentIdx
   * @param {Array<string>} queue
   * @returns {{ nextIdx: number, nextAct: string, isDone: boolean }}
   */
  static advanceShowAct(currentIdx, queue = []) {
    const nextIdx = currentIdx + 1;
    const isDone = nextIdx >= queue.length;
    const nextAct = isDone ? 'done' : queue[nextIdx];
    return { nextIdx, nextAct, isDone };
  }

  /**
   * Creates a clean initial game state object for a new match.
   * @param {Object} [settings={}]
   * @param {Array<string>} [activeSeats=['p1', 'p2']]
   * @returns {Object}
   */
  static getInitialGameState(settings = {}, activeSeats = ['p1', 'p2']) {
    const scores = {};
    const prev = {};
    const handPeg = {};
    for (const sId of activeSeats) {
      scores[sId] = 0;
      prev[sId] = 0;
      handPeg[sId] = 0;
    }
    scores.you = 0;
    scores.cpu = 0;
    prev.you = 0;
    prev.cpu = 0;
    handPeg.you = 0;
    handPeg.cpu = 0;

    return {
      scores,
      prev,
      phase: 'cut',
      over: false,
      winner: null,
      dealer: null,
      turn: null,
      count: 0,
      pile: [],
      played: [],
      handPeg,
      pegLog: [],
      jailed: {},
      atBar: {},
      inTimeout: {},
      inDetour: {},
      inRoundAbout: {},
      deck: [],
      crib: [],
      starter: null,
      starters: [],
      handsCompleted: 0,
      showAct: null,
      recorded: false
    };
  }

  /**
   * Helper to sync legacy 2P aliases (you/cpu) from p1/p2.
   * @private
   */
  static _syncLegacyScoreAliases(state, key) {
    if (!state || !state.scores) return;
    if (key === 'p1' || key === 'team1') {
      state.scores.you = state.scores[key];
      if (state.prev) state.prev.you = state.prev[key];
    } else if (key === 'p2' || key === 'team2') {
      state.scores.cpu = state.scores[key];
      if (state.prev) state.prev.cpu = state.prev[key];
    }
  }

  /**
   * Constructs and returns a complete, fully initialized match state object.
   * Initializes all seat/team scores, obstacle states, pause flags, card containers,
   * and sign configurations if optSigns is enabled.
   * @param {Object} [settings={}]
   * @param {Object} [options={}]
   * @returns {GameState|Object}
   */
  static createInitialMatchState(settings = {}, options = {}) {
    const sc = { p1: 0, p2: 0, p3: 0, p4: 0, p5: 0, p6: 0, p7: 0, p8: 0, team1: 0, team2: 0, team3: 0, team4: 0, you: 0, cpu: 0 };
    const pr = { p1: 0, p2: 0, p3: 0, p4: 0, p5: 0, p6: 0, p7: 0, p8: 0, team1: 0, team2: 0, team3: 0, team4: 0, you: 0, cpu: 0 };

    const stateData = {
      scores: sc,
      prev: pr,
      jailed: {},
      atBar: {},
      inTimeout: {},
      inDetour: {},
      inRoundAbout: {},
      signsState: {},
      lastScoreDelta: {},
      pausedForSign: false,
      pausedForAdultTimer: false,
      pausedForAdultStrip: false,
      pausedForPintsBonus: false,
      _resumeAfterSign: null,
      _resumeAfterAdultTimer: null,
      _resumeAfterPintsBonus: null,
      dealer: null,
      over: false,
      recorded: false,
      hands: { p1: [], p2: [], p3: [], p4: [], p5: [], p6: [], p7: [], p8: [] },
      kept: { p1: [], p2: [], p3: [], p4: [], p5: [], p6: [], p7: [], p8: [] },
      youHand: [],
      cpuHand: [],
      crib: [],
      deckDiscards: [],
      pile: [],
      selected: [],
      starter: null,
      count: 0,
      phase: 'cut',
      discardsComplete: false,
      revealCrib: false,
      stolenCrib: null,
      stolenCribResolved: false,
      twistedCribbageResolved: false,
      twisted8Cards: null,
      starterGuess: null
    };

    if (settings && settings.optSigns) {
      const getSignConfigs = options.getSignConfigs || (typeof window !== 'undefined' && window.getSignConfigs ? window.getSignConfigs : null);
      if (typeof getSignConfigs === 'function') {
        const blocks = getSignConfigs() || [];
        blocks.forEach(b => {
          if (b && b.id && Array.isArray(b.initialSigns)) {
            stateData.signsState[b.id] = [...b.initialSigns];
          }
        });
      }
    }

    const GameStateClass = options.GameState || GameState || (typeof window !== 'undefined' && window.CC && window.CC.GameState);
    return GameStateClass ? new GameStateClass(stateData) : stateData;
  }

  /**
   * Begins the initial match cut ritual to determine the first dealer.
   * @param {Object} [context]
   */
  static beginCut(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const resetAnims = context.resetAnims || (typeof window !== 'undefined' ? window.resetAnims : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const shuffle = context.shuffle || (typeof window !== 'undefined' ? window.shuffle : (arr) => arr);
    const makeDeck = context.makeDeck || (typeof window !== 'undefined' ? window.makeDeck : () => []);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const stepNextCut = context.stepNextCut || (typeof window !== 'undefined' ? window.stepNextCut : GameEngine.stepNextCut);

    resetAnims();
    state.phase = 'cut';
    if (sfx && typeof sfx.shuffle === 'function') sfx.shuffle();
    state.hands = { p1: [], p2: [], p3: [], p4: [] };
    state.youHand = [];
    state.cpuHand = [];
    state.crib = [];
    state.starter = null;
    state.starters = [];
    state.spadesCut = false;
    state.starterGuess = null;
    state.pile = [];
    state.played = [];
    state.selected = [];
    state.count = 0;
    state.cutDeck = shuffle(makeDeck());
    state.cutPicks = {};
    state.highlightIds = [];
    state.cutTurn = 'p1';
    setStatus('Fan the deck and cut — tap how deep. Lowest card deals (Ace is low).');
    render();
    netPush();
    stepNextCut(context);
  }

  /**
   * Renders the interactive cut fan in the match DOM.
   * @param {Object} [context]
   */
  static renderCutFan(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const fan = doc.getElementById('cutFan');
    const hint = doc.getElementById('cutHint');
    if (!fan) return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => String(c.rank));
    const cardEl = context.cardEl || (typeof window !== 'undefined' ? window.cardEl : null);
    const pickCut = context.pickCut || (typeof window !== 'undefined' ? window.pickCut : GameEngine.pickCut);

    const activeSeats = getActiveSeatIds();
    const humanNeedsToCut = activeSeats.some(sId => isSeatHuman(sId) && (!state || !state.cutPicks || !state.cutPicks[sId]));
    const showing = Boolean(state && state.phase === 'cut' && state.cutDeck && state.cutDeck.length &&
      !(state.hands && state.hands.p1 && state.hands.p1.length) && humanNeedsToCut);

    fan.classList.toggle('hidden', !showing);
    if (hint) hint.classList.toggle('hidden', !showing);
    fan.innerHTML = '';
    if (!showing || !state) return;

    const curTurn = state.cutTurn || 'p1';
    const curPick = state.cutPicks && state.cutPicks[curTurn];
    const isPassingDevice = !isOnline() && Boolean(state && state.passingTo);
    const isMyCut = !isPassingDevice && (isOnline()
      ? (!state.cutPicks || !state.cutPicks[mySeat()]) && state.cutTurn === mySeat()
      : (isSeatHuman(curTurn) && !curPick));

    if (hint) {
      if (isOnline()) {
        const myPick = state.cutPicks && state.cutPicks[mySeat()];
        if (myPick) {
          hint.textContent = 'You cut the ' + cardLabel(myPick) + '. Waiting for other players to cut…';
        } else if (isMyCut) {
          hint.textContent = 'Your cut — click a card in the fan (left = top of deck, right = deeper).';
        } else {
          hint.textContent = seatName(curTurn) + ' is cutting the deck…';
        }
      } else {
        if (isMyCut) {
          hint.textContent = (curTurn === 'p1' ? 'Your cut' : seatName(curTurn) + '’s cut') + ' — click a card in the fan (lowest card deals).';
        } else {
          hint.textContent = seatName(curTurn) + ' is cutting the deck…';
        }
      }
    }

    const deck = state.cutDeck;
    if (Array.isArray(deck) && typeof cardEl === 'function') {
      deck.forEach((c, i) => {
        const taken = Object.values(state.cutPicks || {}).some(p => p && p.id === c.id);
        if (taken) return;
        const el = cardEl(c, {
          back: true,
          playable: isMyCut,
          onClick: isMyCut ? () => pickCut(i, isOnline() ? mySeat() : curTurn, context) : undefined,
          anim: 'fan',
          animKey: 'cutfan-' + c.id,
          delay: Math.min(i * 28, 700)
        });
        el.style.zIndex = String(i + 1);
        el.title = isMyCut ? ('Cut ' + (i + 1) + ' cards down') : 'Waiting for cut…';
        fan.appendChild(el);
      });
    }
  }

  /**
   * Resolves cut cards, declarations of initial dealer, and ties.
   * @param {Object} [context]
   */
  static resolveCuts(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => (c ? c.rank : 0));
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const shuffle = context.shuffle || (typeof window !== 'undefined' ? window.shuffle : (arr) => arr);
    const makeDeck = context.makeDeck || (typeof window !== 'undefined' ? window.makeDeck : () => []);
    const stepNextCut = context.stepNextCut || (typeof window !== 'undefined' ? window.stepNextCut : GameEngine.stepNextCut);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => String(c.rank));
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const newHand = context.newHand || (typeof window !== 'undefined' ? window.newHand : GameEngine.newHand);

    const activeSeats = getActiveSeatIds();
    const allCut = activeSeats.every(sId => state.cutPicks && state.cutPicks[sId]);
    if (!allCut) return;

    const entries = activeSeats.map(sId => ({
      seatId: sId,
      card: state.cutPicks[sId],
      rank: rankOf(state.cutPicks[sId])
    }));

    let minRank = 999;
    for (const e of entries) {
      if (e.rank < minRank) minRank = e.rank;
    }
    const minEntries = entries.filter(e => e.rank === minRank);

    if (minEntries.length > 1 || entries.some(e => isWild(e.card))) {
      const tieMsg = 'Cut: Tie for lowest rank or joker — cut again.';
      log(tieMsg);
      setStatus(tieMsg);
      fancyCall('Tie! Cut again', 'go', 1500);
      render();
      netPush({ log: tieMsg, fancy: { text: 'Tie! Cut again', kind: 'go', ms: 1500 } });
      setTimeout(() => {
        if (!state || state.over || (state.phase !== 'cut' && state.phase !== 'cut-reveal')) return;
        state.cutPicks = {};
        state.highlightIds = [];
        state.cutTurn = 'p1';
        state.cutDeck = shuffle(makeDeck());
        state.phase = 'cut';
        render();
        netPush();
        stepNextCut(context);
      }, 2000);
      return;
    }

    const winnerSeat = minEntries[0].seatId;
    state.dealer = winnerSeat;
    state.phase = 'cut-reveal';
    state.highlightIds = [minEntries[0].card.id];
    state.cutDeck = [];

    const cutSummary = entries.map(e => `${seatName(e.seatId)}: ${cardLabel(e.card)}`).join(' vs ');
    const cutMsg = `Cut: ${cutSummary}. ${seatName(winnerSeat)} deals first.`;
    log(cutMsg);

    const myWinnerCall = (winnerSeat === mySeat() ? 'You deal first!' : `${seatName(winnerSeat)} deals first!`);
    fancyCall(myWinnerCall, 'famous', 1800);
    render();
    netPush({
      log: cutMsg,
      fancy: { text: myWinnerCall, kind: 'famous', ms: 1800 }
    });
    setTimeout(() => { if (state && !state.over) newHand(context); }, 3200);
  }

  /**
   * Processes card selection from the cut fan for a player.
   * @param {number} index
   * @param {string} [seat]
   * @param {Object} [context]
   */
  static pickCut(index, seat, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'cut' || !state.cutDeck) return;

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const stepNextCut = context.stepNextCut || (typeof window !== 'undefined' ? window.stepNextCut : GameEngine.stepNextCut);

    const who = seat || (isOnline() ? mySeat() : (state.cutTurn || 'p1'));
    if (isOnline() && settings.netRole === 'guest') {
      netSend({ type: 'intent', op: 'cut', index: index, seatId: mySeat() });
      return;
    }
    if (state.cutPicks && state.cutPicks[who]) return;
    if (!isOnline() && state.cutTurn !== who) return;
    let i = index;
    if (i == null || i < 0 || i >= state.cutDeck.length) return;
    let card = state.cutDeck[i];
    while (card && isWild(card) && i < state.cutDeck.length - 1) {
      i += 1;
      card = state.cutDeck[i];
    }
    if (!card || isWild(card)) {
      setStatus('That cut is a joker — pick another depth.');
      return;
    }
    const used = Object.values(state.cutPicks || {}).filter(Boolean).map(c => c.id);
    if (used.includes(card.id)) return;
    if (!state.cutPicks) state.cutPicks = {};
    state.cutPicks[who] = card;
    if (sfx && typeof sfx.deal === 'function') sfx.deal();

    render();
    netPush();

    stepNextCut(context);
  }

  /**
   * Advances turn progression through cutting the deck for the initial deal.
   * @param {Object} [context]
   */
  static stepNextCut(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'cut') return;

    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const updateTurnPrompt = context.updateTurnPrompt || (typeof window !== 'undefined' ? window.updateTurnPrompt : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (seat, cb) => cb && cb());
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const resolveCuts = context.resolveCuts || (typeof window !== 'undefined' ? window.resolveCuts : GameEngine.resolveCuts);

    const activeSeats = getActiveSeatIds();
    const allCut = activeSeats.every(sId => state.cutPicks && state.cutPicks[sId]);
    if (allCut) {
      resolveCuts(context);
      return;
    }

    const nextSeat = activeSeats.find(sId => !state.cutPicks[sId]);
    if (!nextSeat) {
      resolveCuts(context);
      return;
    }

    state.cutTurn = nextSeat;

    if (isSeatCpu(nextSeat)) {
      const cutMsg = seatName(nextSeat) + ' is cutting the deck…';
      setStatus(cutMsg);
      updateTurnPrompt(cutMsg);
      render();
      netPush();

      setTimeout(() => {
        if (!state || state.phase !== 'cut') return;
        const usedIds = Object.values(state.cutPicks || {}).filter(Boolean).map(c => c.id);
        const rest = (state.cutDeck || []).filter(c => !usedIds.includes(c.id) && !isWild(c));
        if (!rest.length) {
          resolveCuts(context);
          return;
        }
        const chosen = rest[Math.floor(Math.random() * rest.length)] || rest[0];
        state.cutPicks[nextSeat] = chosen;
        if (sfx && typeof sfx.deal === 'function') sfx.deal();
        render();
        netPush();

        setTimeout(() => {
          if (!state || state.phase !== 'cut') return;
          GameEngine.stepNextCut(context);
        }, 500);
      }, 750);
    } else if (isSeatHuman(nextSeat)) {
      const cutMsg = (nextSeat === 'p1' ? 'Your cut' : `${seatName(nextSeat)}'s cut`) + ' — tap any card in the fan to cut the deck.';
      setStatus(cutMsg);
      updateTurnPrompt(cutMsg);
      if (!isOnline() && nextSeat !== 'p1') {
        passTo(nextSeat, () => {
          render();
          netPush();
        });
      } else {
        render();
        netPush();
      }
    }
  }

  /**
   * Helper to perform a quick midpoint cut.
   * @param {Object} [context]
   */
  static doCut(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const pickCut = context.pickCut || (typeof window !== 'undefined' ? window.pickCut : GameEngine.pickCut);
    const len = (state && state.cutDeck) ? state.cutDeck.length : 0;
    pickCut(Math.floor(len / 2), null, context);
  }

  /**
   * Initiates a new hand, deals cards, resets hand pegging state, and prepares discards.
   * @param {Object} [context]
   */
  static newHand(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.over) return;

    const resetAnims = context.resetAnims || (typeof window !== 'undefined' ? window.resetAnims : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const DeckClass = context.Deck || (typeof Deck !== 'undefined' ? Deck : (typeof window !== 'undefined' ? window.Deck : null));
    const shuffle = context.shuffle || (typeof window !== 'undefined' ? window.shuffle : (arr) => arr);
    const makeDeck = context.makeDeck || (typeof window !== 'undefined' ? window.makeDeck : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => s === 'p1' ? 'p2' : 'p1');
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const finishDeal = context.finishDeal || (typeof window !== 'undefined' ? window.finishDeal : GameEngine.finishDeal);

    resetAnims();
    if (sfx && typeof sfx.shuffle === 'function') sfx.shuffle();

    const activeSeats = getActiveSeatIds();
    const count = settings.playerCount || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
    const dealN = isCanadianDoubles ? 10 : (count >= 3 ? 5 : (is7Card ? 7 : (settings.deal === 'super' ? 9 : 6)));
    const deckObj = DeckClass ? new DeckClass({ decks: settings.decks || 1, jokers: Boolean(settings && settings.jokers) }).shuffle() : null;
    const deck = deckObj ? deckObj.cards : shuffle(makeDeck());

    state.deckObj = deckObj;
    state.deck = deck;
    state.hands = {};
    state.seatHands = {};
    state.kept = {};
    state.seatKept = {};
    state.youKept = null;
    state.cpuKept = null;
    state.crib = [];
    state.deckDiscards = [];
    state.seatDiscards = {};
    state.stolenCrib = null;
    state.stolenCribResolved = false;
    state.twistedCribbageResolved = false;
    state.twisted8Cards = null;

    // In 3-player, or in 2-player 7 Card Cribbage, deal 1 card face down directly to crib
    if (count === 3 || is7Card) {
      state.crib = [deck.pop()];
    }

    if (isCanadianDoubles) {
      const pone = nextSeat(state.dealer);
      const dealer = state.dealer;
      for (const sId of activeSeats) {
        if (sId === pone || sId === dealer) {
          const cards = deck.splice(0, 10);
          setSeatHand(sId, cards);
        } else {
          setSeatHand(sId, []);
        }
      }
      log(`Canadian Doubles: 10 cards dealt to Pone (${seatName(pone)}) and Dealer (${seatName(dealer)}). Partners await passed hands.`);
    } else {
      for (const sId of activeSeats) {
        const cards = deck.splice(0, dealN);
        setSeatHand(sId, cards);
      }
    }

    if (is7Card) {
      log('7 Card Cribbage: 7 cards dealt to each player, 1 card dealt to the crib.');
    }

    if (sfx) {
      const slideFn = typeof sfx.cardSlide === 'function' ? () => sfx.cardSlide() : (typeof sfx.deal === 'function' ? () => sfx.deal() : null);
      if (slideFn) {
        const slideCount = Math.min(dealN, 6);
        for (let i = 0; i < slideCount; i++) {
          setTimeout(slideFn, 70 + i * 85);
        }
      }
    }

    state.starter = null;
    state.starters = [];
    state.spadesCut = false;
    state.starterGuess = null;
    state.discardsComplete = false;
    state.selected = [];
    state.pile = [];
    state.played = [];
    state.count = 0;
    state.turn = nextSeat(state.dealer);
    state.assignTarget = null;
    state.goPending = null;
    state.lastPlayed = null;
    state.handPeg = { p1: 0, p2: 0, p3: 0, p4: 0, p5: 0, p6: 0, p7: 0, p8: 0, team1: 0, team2: 0, team3: 0, team4: 0, you: 0, cpu: 0 };
    state.handRecorded = false;
    state.pegLog = [];
    state.showAct = null;
    state.lastShow = null;
    state.showQueue = null;
    state.showMeter = null;
    state.cutDeck = null;
    state.cutPicks = {};
    state.cutTurn = null;
    state.highlightIds = [];
    state.currentHumanDiscard = null;
    state.phase = 'dealing';

    if (isSoloVsPairs()) {
      const p1Name = seatName('p1');
      if (settings.playerCount === 3) {
        log(`3 Players (1 vs 2) — Solo: ${p1Name} (always scores crib) · Pair: ${seatName('p2')} & ${seatName('p3')}`);
      } else if (settings.playerCount === 5) {
        log(`5 Players (1 vs 2 Pairs) — Solo: ${p1Name} (always scores crib) · Pair 1: ${seatName('p2')} & ${seatName('p4')} · Pair 2: ${seatName('p3')} & ${seatName('p5')}`);
      } else if (settings.playerCount === 7) {
        log(`7 Players (1 vs 3 Pairs) — Solo: ${p1Name} (always scores crib) · Pairs: (P2 & P5), (P3 & P6), (P4 & P7)`);
      }
    }

    if (count === 5) {
      log(`5 Players — 4 players discard to crib · Dealer (${seatName(state.dealer)}) discards to the deck.`);
    } else if (count === 6) {
      log(`6 Players — 4 players discard to crib · 5th player & Dealer (${seatName(state.dealer)}) discard to the deck.`);
    } else if (count === 7) {
      log(`7 Players — 4 players discard to crib · 5th, 6th players & Dealer (${seatName(state.dealer)}) discard to the deck.`);
    } else if (count === 8) {
      log(`8 Players — 4 players discard to crib · 5th, 6th, 7th players & Dealer (${seatName(state.dealer)}) discard to the deck.`);
    }

    setStatus(seatName(state.dealer) + ' deals…');
    fancyCall('Dealing', '', 700);
    render();

    setTimeout(() => {
      finishDeal(context);
    }, 650);
  }

  /**
   * Concludes deal animation and transitions to discard or joker assignment.
   * @param {Object} [context]
   */
  static finishDeal(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const cpuAssignWilds = context.cpuAssignWilds || (typeof window !== 'undefined' ? window.cpuAssignWilds : () => {});
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : null);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const startDiscardOrPickFlow = context.startDiscardOrPickFlow || (typeof window !== 'undefined' ? window.startDiscardOrPickFlow : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    // Wild assignment for CPUs
    for (const sId of getActiveSeatIds()) {
      if (isSeatCpu(sId)) {
        const h = getSeatHand(sId);
        if (h && h.length) cpuAssignWilds(h);
      }
    }

    if (settings.optCutBeforeDiscard) {
      beginCutStarter(context);
      return;
    }

    state.phase = 'discard';

    // If P1 was dealt a joker:
    const p1Wilds = (getSeatHand('p1') || []).filter(c => isWild(c));
    if (settings.jokers && p1Wilds.length >= 3) {
      AchievementManager.unlock('triple_joker', { sfx });
      AchievementManager.unlock('super_joker', { sfx });
    } else if (settings.jokers && p1Wilds.length >= 2) {
      AchievementManager.unlock('super_joker', { sfx });
    }
    if (settings.jokers && p1Wilds.some(c => !c.playAs)) {
      state.phase = 'assign';
      state.assignTarget = 'hand';
      setStatus('You were dealt a joker. Click it and choose any card.');
      render();
      const firstWild = p1Wilds.find(c => !c.playAs);
      if (typeof openWildPick === 'function' && firstWild) {
        openWildPick(firstWild, 'hand', context);
      }
    } else {
      startDiscardOrPickFlow(context);
    }
    netPush();
  }

  /**
   * Prompts or executes the pone starter card cut.
   * @param {Object} [context]
   */
  static beginCutStarter(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => s === 'p1' ? 'p2' : 'p1');
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const poneDoCutStarter = context.poneDoCutStarter || (typeof window !== 'undefined' ? window.poneDoCutStarter : GameEngine.poneDoCutStarter);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});

    const pone = nextSeat(state.dealer);
    state.phase = 'cut-starter';
    state.cutTurn = pone;
    state.selected = [];
    const me = typeof mySeat === 'function' ? mySeat() : (mySeat || 'p1');
    const isMyCut = pone === me || (!isOnline() && isSeatHuman(pone));
    const isGuessOpt = Boolean(settings && settings.optGuessStarter);

    setStatus(isMyCut
      ? (isGuessOpt
          ? (pone === me ? 'You are the pone — guess the starter card rank, then cut the deck!' : `${seatName(pone)} (pone): guess the starter rank and cut!`)
          : (pone === me ? 'You are the pone — cut the deck for the starter card.' : `${seatName(pone)} (pone): cut the deck for the starter card.`))
      : (isGuessOpt
          ? `${seatName(pone)} (pone) is guessing and cutting the starter…`
          : `${seatName(pone)} (pone) is cutting the starter…`));
    render();
    netPush();

    if (isSeatCpu(pone)) {
      setTimeout(() => {
        if (!state || state.phase !== 'cut-starter' || state.cutTurn !== pone) return;
        poneDoCutStarter(context);
      }, 850);
    } else if (isMyCut) {
      if (isGuessOpt && !state.starterGuess) {
        setTimeout(() => {
          if (!state || state.phase !== 'cut-starter' || state.starterGuess) return;
          const promptFn = context.openGuessStarterPrompt || (typeof window !== 'undefined' ? (window.modalManager?.openGuessStarterPrompt || window.openGuessStarterPrompt || window.CrazyCribbage?.ModalManager?.openGuessStarterPrompt) : null) || ModalManager.openGuessStarterPrompt;
          if (typeof promptFn === 'function') {
            promptFn(pone, (rank) => {
              state.starterGuess = { who: pone, rank };
              GameEngine.poneDoCutStarter(context);
            }, context);
          }
        }, 300);
      }
    } else if (!isOnline() && pone !== 'p1' && isSeatHuman(pone)) {
      passTo(pone, () => {
        render();
        if (isGuessOpt && !state.starterGuess) {
          setTimeout(() => {
            if (!state || state.phase !== 'cut-starter' || state.starterGuess) return;
            const promptFn = context.openGuessStarterPrompt || (typeof window !== 'undefined' ? (window.modalManager?.openGuessStarterPrompt || window.openGuessStarterPrompt || window.CrazyCribbage?.ModalManager?.openGuessStarterPrompt) : null) || ModalManager.openGuessStarterPrompt;
            if (typeof promptFn === 'function') {
              promptFn(pone, (rank) => {
                state.starterGuess = { who: pone, rank };
                GameEngine.poneDoCutStarter(context);
              }, context);
            }
          }, 300);
        }
      });
    }
  }

  /**
   * Generates a starter rank guess (1..13) for CPU pone.
   * CPU counts remaining unseen ranks from its perspective to pick a rank with maximum probability.
   * @param {string} poneSeat
   * @param {Object} state
   * @param {Object} [settings]
   * @param {Object} [context]
   * @returns {number} Rank 1..13 (Ace=1 .. King=13)
   */
  static cpuGuessStarterRank(poneSeat, state, settings = {}, context = {}) {
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : null);
    const decks = (settings && Number(settings.decks)) || 1;
    const rankCounts = {};
    for (let r = 1; r <= 13; r++) {
      rankCounts[r] = 4 * decks;
    }

    const seenCards = [];
    if (state) {
      if (getSeatHand) {
        seenCards.push(...(getSeatHand(poneSeat) || []));
      } else if (state.hands && state.hands[poneSeat]) {
        seenCards.push(...state.hands[poneSeat]);
      } else if (poneSeat === 'p2' && state.cpuHand) {
        seenCards.push(...state.cpuHand);
      }
      if (state.seatDiscards && state.seatDiscards[poneSeat]) {
        seenCards.push(...state.seatDiscards[poneSeat]);
      }
    }

    for (const c of seenCards) {
      const r = (c && (c.r || c.rank)) || 0;
      if (r >= 1 && r <= 13 && rankCounts[r]) {
        rankCounts[r]--;
      }
    }

    let maxRemaining = -1;
    let bestRanks = [];
    for (let r = 1; r <= 13; r++) {
      const count = rankCounts[r];
      if (count > maxRemaining) {
        maxRemaining = count;
        bestRanks = [r];
      } else if (count === maxRemaining) {
        bestRanks.push(r);
      }
    }

    const preferred = [5, 7, 8, 6, 4];
    for (const pref of preferred) {
      if (bestRanks.includes(pref)) return pref;
    }
    const idx = Math.floor(Math.random() * bestRanks.length);
    return bestRanks[idx] || 5;
  }

  /**
   * Cuts starter card when triggered by pone.
   * @param {Object} [context]
   */
  static poneDoCutStarter(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'cut-starter') return;

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => s === 'p1' ? 'p2' : 'p1');
    const openGuessStarterPrompt = context.openGuessStarterPrompt || (typeof window !== 'undefined' ? (window.modalManager?.openGuessStarterPrompt || window.openGuessStarterPrompt || window.CrazyCribbage?.ModalManager?.openGuessStarterPrompt) : null) || ModalManager.openGuessStarterPrompt;

    const pone = (state.cutTurn && state.phase === 'cut-starter') ? state.cutTurn : nextSeat(state.dealer);

    if (settings && settings.optGuessStarter && !state.starterGuess) {
      if (isSeatCpu(pone)) {
        const guess = GameEngine.cpuGuessStarterRank(pone, state, settings, context);
        state.starterGuess = { who: pone, rank: guess };
      } else if (isSeatHuman(pone)) {
        if (isOnline() && settings.netRole === 'guest') {
          if (typeof openGuessStarterPrompt === 'function') {
            openGuessStarterPrompt(pone, (rank) => {
              state.starterGuess = { who: pone, rank };
              netSend({ type: 'intent', op: 'cutStarter', seatId: mySeat(), guessRank: rank });
            }, context);
            return;
          } else {
            state.starterGuess = { who: pone, rank: 5 };
            netSend({ type: 'intent', op: 'cutStarter', seatId: mySeat(), guessRank: 5 });
            return;
          }
        }
        if (typeof openGuessStarterPrompt === 'function') {
          openGuessStarterPrompt(pone, (rank) => {
            state.starterGuess = { who: pone, rank };
            GameEngine.poneDoCutStarter(context);
          }, context);
          return;
        } else {
          state.starterGuess = { who: pone, rank: 5 };
        }
      }
    }

    if (isOnline() && settings.netRole === 'guest') {
      const gRank = state.starterGuess ? state.starterGuess.rank : null;
      netSend({ type: 'intent', op: 'cutStarter', seatId: mySeat(), guessRank: gRank });
      return;
    }
    if (sfx && typeof sfx.snap === 'function') sfx.snap();
    startStarter(context);
  }

  /**
   * Cuts the starter card from the deck and initiates the starter ritual animation.
   * @param {Object} [context]
   */
  static startStarter(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});

    // If Guess Starter is enabled and the starter guess has not occurred yet, route to beginCutStarter!
    if (settings && settings.optGuessStarter && !state.starterGuess) {
      const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);
      if (typeof beginCutStarter === 'function') {
        beginCutStarter(context);
        return;
      }
    }

    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const finishStarterRitual = context.finishStarterRitual || (typeof window !== 'undefined' ? window.finishStarterRitual : GameEngine.finishStarterRitual);
    const doc = context.document || (typeof document !== 'undefined' ? document : null);

    hideWait();
    state.selected = [];
    const isProgCut = Boolean(settings && settings.optProgressiveCut && (settings.playerCount || 2) === 2);
    const cutCount = isProgCut ? Math.max(1, Math.floor((state.handsCompleted || 0) / 2) + 1) : 1;

    state.starters = [];
    for (let i = 0; i < cutCount; i++) {
      if (state.deck && state.deck.length > 2) {
        const cutIdx = Math.floor(Math.random() * (state.deck.length - 1));
        state.starters.push(state.deck.splice(cutIdx, 1)[0]);
      } else if (state.deck && state.deck.length > 0) {
        state.starters.push(state.deck.pop());
      }
    }
    state.starter = state.starters[0] || null;
    state.phase = 'starter-ritual';
    state.starterFace = false;
    const starterWord = cutCount > 1 ? `Cutting ${cutCount} starters…` : 'Cutting the starter…';
    const fancyWord = cutCount > 1 ? `${cutCount} Starters` : 'Starter';
    setStatus(starterWord);
    fancyCall(fancyWord, '', 800);
    render();
    netPush({ fancy: { text: fancyWord, kind: '', ms: 800 } });
    setTimeout(() => {
      if (!state || state.phase !== 'starter-ritual') return;
      state.starterFace = true;
      render();
      netPush();
      setTimeout(() => {
        const cards = doc ? doc.querySelectorAll('#starterZone .playing-card') : [];
        if (cards && cards.length) {
          cards.forEach(c => c.classList.add('starter-tap'));
        }
        setTimeout(() => {
          finishStarterRitual(context);
        }, 430);
      }, 560);
    }, 520);
  }

  /**
   * Concludes the starter card ritual, handling jokers or proceeding to match play.
   * @param {Object} [context]
   */
  static finishStarterRitual(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || !state.starter) return;

    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : () => {});
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const cpuAssignWilds = context.cpuAssignWilds || (typeof window !== 'undefined' ? window.cpuAssignWilds : () => {});
    const afterStarterAssigned = context.afterStarterAssigned || (typeof window !== 'undefined' ? window.afterStarterAssigned : () => {});

    const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
    const unassignedWild = starters.find(c => isWild(c) && !c.playAs);
    if (unassignedWild) {
      const dealerHuman = isSeatHuman(state.dealer);
      if (dealerHuman) {
        const go = () => {
          state.phase = 'assign';
          state.assignTarget = 'starter';
          render();
          openWildPick(unassignedWild, 'starter');
          hideWait();
          setStatus(seatName(state.dealer) + ': starter is a joker. Choose the card it becomes.');
        };
        if (state.dealer !== 'p1' && isHvH()) passTo(state.dealer, go);
        else go();
        return;
      }
      cpuAssignWilds([unassignedWild]);
    }
    afterStarterAssigned(context);
  }

  /**
   * Initializes state and prompts for the pegging play phase.
   * @param {Object} [context]
   */
  static startPlayAfterDiscards(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const activeSeats = (typeof context.getActiveSeatIds === 'function')
      ? context.getActiveSeatIds()
      : (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function' ? window.getActiveSeatIds() : ['p1', 'p2']);
    const nextSeat = context.nextSeat
      || (typeof window !== 'undefined' && typeof window.nextSeat === 'function' ? window.nextSeat : (s) => GameEngine.advanceDealer(s, activeSeats));
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());

    state.phase = 'play';
    const cribEl = (typeof document !== 'undefined') ? document.getElementById('cribCards') : null;
    if (cribEl) {
      cribEl.classList.remove('counting');
      cribEl.classList.add('playing');
    }
    state.count = 0;
    state.pile = [];
    const leadSeat = nextSeat(state.dealer);
    state.turn = leadSeat;
    hideWait();
    render();
    netPush();
    if (leadSeat && isSeatCpu(leadSeat)) {
      scheduleOppPlay(leadSeat, context);
    } else if (leadSeat && isSeatHuman(leadSeat)) {
      GameEngine.announcePlayTurn(leadSeat, context);
      if (!isOnline()) {
        if (leadSeat !== 'p1') {
          passTo(leadSeat, () => {
            render();
          }, context);
        } else {
          render();
        }
      }
    }
  }

  /**
   * Confirms P2 discard in 2-player pass and play or guest online mode.
   * @param {Object} [context]
   */
  static confirmP2Discard(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

    if (isOnline() && settings.netRole === 'guest') {
      netSend({ type: 'intent', op: 'discardP2', ids: (state.selected || []).slice() });
      state.selected = [];
      render();
      return;
    }
    const count = parseInt(settings && settings.playerCount, 10) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const discardN = is7Card ? 2 : (settings.deal === 'super' ? 3 : 2);
    if (!state.selected || state.selected.length !== discardN) return;
    const ids = new Set(state.selected);
    const cpuHand = state.cpuHand || [];
    const thrown = cpuHand.filter(c => ids.has(c.id));
    state.cpuHand = cpuHand.filter(c => !ids.has(c.id));
    state.cpuKept = state.cpuHand.slice();
    state.seatDiscards = state.seatDiscards || {};
    state.seatDiscards.p2 = thrown;
    state.crib = state.crib || [];
    state.crib.push(...thrown);
    state.selected = [];
    if (settings.optCutBeforeDiscard) {
      state.discardsComplete = true;
      startPlayAfterDiscards(context);
    } else if (settings.optGuessStarter) {
      state.discardsComplete = true;
      const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);
      beginCutStarter(context);
    } else {
      startStarter(context);
    }
  }

  /**
   * Handles dealer card selection from pone hands during Pick Your Crib.
   * @param {string} seatId
   * @param {Object} c
   * @param {Object} [context]
   */
  static onPickCribCard(seatId, c, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'pick-crib' || state.currentPickPone !== seatId) return;
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const pickN = state.pickN || 2;
    state.selected = state.selected || [];
    const idx = state.selected.indexOf(c.id);
    if (idx >= 0) {
      state.selected.splice(idx, 1);
    } else if (state.selected.length < pickN) {
      state.selected.push(c.id);
    }
    render();
  }

  /**
   * Prompts human dealer to select cards from active pone hands.
   * @param {Object} [context]
   */
  static startHumanPickCrib(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const finishPickYourCrib = context.finishPickYourCrib || (typeof window !== 'undefined' ? window.finishPickYourCrib : GameEngine.finishPickYourCrib);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});

    if (!state.pickPones || !state.pickPones.length) {
      finishPickYourCrib(context);
      return;
    }
    state.phase = 'pick-crib';
    state.currentPickPone = state.pickPones[0];
    state.selected = [];
    const count = parseInt(settings.playerCount, 10) || 2;
    const pickN = count >= 3 ? 1 : (settings.deal === 'super' ? 3 : 2);
    state.pickN = pickN;
    setStatus(`Pick your Crib: Select ${pickN} card${pickN > 1 ? 's' : ''} from ${seatName(state.currentPickPone)}'s hand to take for your crib.`);
    render();
  }

  /**
   * Confirms human dealer theft of selected pone cards into crib.
   * @param {Object} [context]
   */
  static confirmPickCrib(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'pick-crib') return;

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const startHumanPickCrib = context.startHumanPickCrib || (typeof window !== 'undefined' ? window.startHumanPickCrib : GameEngine.startHumanPickCrib);
    const finishPickYourCrib = context.finishPickYourCrib || (typeof window !== 'undefined' ? window.finishPickYourCrib : GameEngine.finishPickYourCrib);

    const pId = state.currentPickPone;
    const pickN = state.pickN || 2;
    if (!state.selected || state.selected.length !== pickN) return;
    const pHand = getSeatHand(pId);
    const ids = new Set(state.selected);
    const picked = pHand.filter(c => ids.has(c.id));
    const remaining = pHand.filter(c => !ids.has(c.id));
    setSeatHand(pId, remaining);
    setSeatKept(pId, remaining.slice());
    state.crib = state.crib || [];
    state.crib.push(...picked);
    state.seatDiscards = state.seatDiscards || {};
    state.seatDiscards[pId] = picked;
    log(`You took ${pickN} card${pickN > 1 ? 's' : ''} from ${seatName(pId)}'s hand for your crib.`);
    fancyCall(`Took ${pickN} card${pickN > 1 ? 's' : ''}`, 'combo', 1200);
    state.selected = [];
    state.pickPones.shift();
    if (state.pickPones.length) {
      startHumanPickCrib(context);
    } else {
      finishPickYourCrib(context);
    }
  }

  /**
   * Concludes the Pick Your Crib sequence and transitions to starter cut or play.
   * @param {Object} [context]
   */
  static finishPickYourCrib(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

    state.phase = 'discard';
    state.currentHumanDiscard = null;
    state.discardsComplete = true;
    if (settings.optCutBeforeDiscard) {
      startPlayAfterDiscards(context);
    } else {
      startStarter(context);
    }
  }

  /**
   * Initiates Draw Cribbage Phase 1 (0-5 cards from 6) or Phase 2 (0-3 cards from 4).
   * @param {number} phaseNum 1 or 2
   * @param {Object} [context]
   */
  static startDrawCribbagePhase(phaseNum, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const isDealer = state.dealer === 'p1' || state.dealer === 'you';
    const pone = isDealer ? 'p2' : 'p1';
    const dealer = isDealer ? 'p1' : 'p2';
    state.drawPhase = phaseNum;
    state.drawSeats = [pone, dealer];
    state.selected = [];
    GameEngine.processNextDrawSeat(context);
  }

  /**
   * Advances Draw Cribbage to the next seat or next phase.
   * @param {Object} [context]
   */
  static processNextDrawSeat(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isTwoHuman = context.isTwoHuman || (typeof window !== 'undefined' ? window.isTwoHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const beginDiscard = context.beginDiscard || (typeof window !== 'undefined' ? window.beginDiscard : GameEngine.beginDiscard);
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

    if (!state.drawSeats || state.drawSeats.length === 0) {
      state.currentDrawSeat = null;
      if (state.drawPhase === 1) {
        state.phase = 'discard';
        state.selected = [];
        if (isTwoHuman()) {
          const pone = (state.dealer === 'p1' || state.dealer === 'you') ? 'p2' : 'p1';
          if (pone === 'p2') {
            passTo('p2', () => {
              state.currentHumanDiscard = 'p2';
              beginDiscard(context);
            });
            return;
          }
          state.currentHumanDiscard = 'p1';
          beginDiscard(context);
        } else {
          state.currentHumanDiscard = 'p1';
          beginDiscard(context);
        }
      } else {
        state.currentHumanDiscard = null;
        state.discardsComplete = true;
        if (settings.optCutBeforeDiscard) {
          startPlayAfterDiscards(context);
        } else {
          startStarter(context);
        }
      }
      return;
    }

    const curSeat = state.drawSeats.shift();
    state.currentDrawSeat = curSeat;
    state.currentHumanDiscard = curSeat;
    state.selected = [];

    if (isSeatCpu(curSeat)) {
      const hand = (getSeatHand(curSeat) || []).slice();
      let cpuThrow = [];
      const lowball = settings.goal === 'low';
      const isDealer = curSeat === state.dealer;
      const aiCls = context.CribbageAI || (typeof CribbageAI !== 'undefined' ? CribbageAI : (typeof window !== 'undefined' ? (window.CrazyCribbage?.CribbageAI || window.CC?.CribbageAI) : null));
      if (state.drawPhase === 1) {
        cpuThrow = (aiCls && aiCls.chooseDrawPhase1)
          ? aiCls.chooseDrawPhase1(hand, isDealer, lowball)
          : [];
      } else {
        cpuThrow = (aiCls && aiCls.chooseDrawPhase2)
          ? aiCls.chooseDrawPhase2(hand, isDealer, lowball, state.starter)
          : [];
      }

      const throwIds = new Set(cpuThrow.map(c => c.id));
      const kept = hand.filter(c => !throwIds.has(c.id));
      const drawn = [];
      for (let i = 0; i < cpuThrow.length; i++) {
        if (state.deck && state.deck.length > 0) {
          drawn.push(state.deck.pop());
        }
      }
      if (cpuThrow.length > 0 && state.deck) {
        state.deck.unshift(...cpuThrow);
      }
      const newHand = kept.concat(drawn);
      setSeatHand(curSeat, newHand);
      setSeatKept(curSeat, newHand.slice());

      if (cpuThrow.length > 0) {
        log(`${seatName(curSeat)} discarded ${cpuThrow.length} card${cpuThrow.length === 1 ? '' : 's'} and drew ${drawn.length} new card${drawn.length === 1 ? '' : 's'}.`);
      } else {
        log(`${seatName(curSeat)} stood pat (kept all cards).`);
      }

      GameEngine.processNextDrawSeat(context);
      return;
    }

    // Human player
    if (curSeat === 'p2' && isTwoHuman()) {
      passTo('p2', () => {
        state.phase = state.drawPhase === 1 ? 'draw-1-p2' : 'draw-2-p2';
        state.selected = [];
        const maxDraw = state.drawPhase === 1 ? 5 : 3;
        setStatus(`Draw Cribbage (${state.drawPhase === 1 ? '1st Draw' : '2nd Draw'}): Select 0–${maxDraw} cards to replace, then click 'Discard & Draw' (or 'Keep Hand').`);
        render();
      });
      return;
    }

    state.phase = state.drawPhase === 1 ? 'draw-1' : 'draw-2';
    state.selected = [];
    const maxDraw = state.drawPhase === 1 ? 5 : 3;
    setStatus(`Draw Cribbage (${state.drawPhase === 1 ? '1st Draw' : '2nd Draw'}): Select 0–${maxDraw} cards to replace, then click 'Discard & Draw' (or 'Keep Hand').`);
    render();
  }

  /**
   * Confirms human draw action in Draw Cribbage.
   * Discards selected cards (returning to deck bottom) and draws replacement cards from top of deck.
   * @param {Object} [context]
   */
  static confirmDraw(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || (state.phase !== 'draw-1' && state.phase !== 'draw-2' && state.phase !== 'draw-1-p2' && state.phase !== 'draw-2-p2')) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const me = mySeat();

    if (isOnline()) {
      if (settings.netRole === 'guest') {
        netSend({ type: 'intent', op: 'draw', ids: state.selected || [], seatId: me });
        state.selected = [];
        const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
        render();
        return;
      }
    }

    const curSeat = state.currentDrawSeat || state.currentHumanDiscard || 'p1';
    const isFirstDraw = state.phase === 'draw-1' || state.phase === 'draw-1-p2';
    const maxDraw = isFirstDraw ? 5 : 3;

    state.selected = state.selected || [];
    if (state.selected.length > maxDraw) return;

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    const selIds = new Set(state.selected);
    const currHand = (getSeatHand(curSeat) || []).slice();
    const discarded = currHand.filter(c => selIds.has(c.id));
    const kept = currHand.filter(c => !selIds.has(c.id));

    const drawn = [];
    for (let i = 0; i < discarded.length; i++) {
      if (state.deck && state.deck.length > 0) {
        drawn.push(state.deck.pop());
      }
    }
    if (discarded.length > 0 && state.deck) {
      state.deck.unshift(...discarded);
    }

    const newHand = kept.concat(drawn);
    setSeatHand(curSeat, newHand);
    setSeatKept(curSeat, newHand.slice());
    state.selected = [];

    if (discarded.length > 0) {
      log(`${seatName(curSeat)} discarded ${discarded.length} card${discarded.length === 1 ? '' : 's'} and drew ${drawn.length} new card${drawn.length === 1 ? '' : 's'}.`);
      fancyCall(`Drew ${drawn.length} card${drawn.length === 1 ? '' : 's'}!`, 'deal', 1200);
    } else {
      log(`${seatName(curSeat)} stood pat (kept all cards).`);
      fancyCall('Stood Pat!', 'deal', 1000);
    }

    if (isOnline()) {
      netPush();
    }

    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    render();

    GameEngine.processNextDrawSeat(context);
  }

  /**
   * Performs blind/random pick from pone hand for CPU dealer during Pick Your Crib.
   * @param {string} dealerSeat
   * @param {string} poneSeat
   * @param {number} pickN
   * @param {Object} [context]
   * @returns {Array}
   */
  static cpuPickFromPone(dealerSeat, poneSeat, pickN, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});

    const poneHand = getSeatHand(poneSeat) || [];
    if (poneHand.length <= pickN) return poneHand.slice();

    // Dealer does not see the front of the cards: blind/random pick
    const shuffled = poneHand.slice().sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, pickN);
    const pickIds = new Set(picked.map(c => c.id));
    const remaining = poneHand.filter(c => !pickIds.has(c.id));
    setSeatHand(poneSeat, remaining);
    setSeatKept(poneSeat, remaining.slice());
    if (state) {
      state.seatDiscards = state.seatDiscards || {};
      state.seatDiscards[poneSeat] = picked;
      state.crib = state.crib || [];
      state.crib.push(...picked);
    }

    log(`${seatName(dealerSeat)} picked ${pickN} card${pickN > 1 ? 's' : ''} from ${seatName(poneSeat)}'s hand for the crib.`);
    return picked;
  }

  /**
   * Coordinates complete CPU dealer Pick Your Crib process.
   * @param {Object} [context]
   */
  static cpuDoPickYourCrib(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const chooseDiscard = context.chooseDiscard || (typeof window !== 'undefined' ? window.chooseDiscard : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const cpuPickFromPone = context.cpuPickFromPone || (typeof window !== 'undefined' ? window.cpuPickFromPone : GameEngine.cpuPickFromPone);
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => String(c.rank));
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

    state.phase = 'discard';
    const count = parseInt(settings.playerCount, 10) || 2;
    const discardN = count >= 3 ? 1 : (settings.deal === 'super' ? 3 : 2);

    // 1. CPU dealer discards its own cards to the crib
    const dealerHand = getSeatHand(state.dealer);
    const cpuThrow = chooseDiscard(dealerHand, true, settings.goal === 'low', discardN, false);
    const cids = new Set(cpuThrow.map(c => c.id));
    const dealerKept = dealerHand.filter(c => !cids.has(c.id));
    setSeatHand(state.dealer, dealerKept);
    setSeatKept(state.dealer, dealerKept.slice());
    state.crib = state.crib || [];
    state.crib.push(...cpuThrow);
    state.seatDiscards = state.seatDiscards || {};
    state.seatDiscards[state.dealer] = cpuThrow;

    // 2. CPU dealer picks from each pone
    const pones = getActiveSeatIds().filter(sId => sId !== state.dealer);
    let stolenFromHuman = [];
    for (const pId of pones) {
      const picked = cpuPickFromPone(state.dealer, pId, discardN, context);
      if (pId === 'p1') stolenFromHuman = picked;
    }

    // 3. Status and notification
    if (stolenFromHuman && stolenFromHuman.length) {
      fancyCall(seatName(state.dealer) + ' took ' + stolenFromHuman.map(cardLabel).join(', '), 'combo', 1800);
      setStatus(seatName(state.dealer) + ' picked ' + stolenFromHuman.map(cardLabel).join(', ') + ' from your hand for their crib.');
    } else {
      setStatus(seatName(state.dealer) + ' picked cards from the pone hand(s) for the crib.');
    }
    render();

    setTimeout(() => {
      if (!state || state.over) return;
      state.discardsComplete = true;
      if (settings.optCutBeforeDiscard) {
        startPlayAfterDiscards(context);
      } else if (settings.optGuessStarter) {
        const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);
        beginCutStarter(context);
      } else {
        startStarter(context);
      }
    }, 1400);
  }

  /**
   * Routes match start to either Pick Your Crib flow or regular discard.
   * @param {Object} [context]
   */
  static startDiscardOrPickFlow(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isTwoHuman = context.isTwoHuman || (typeof window !== 'undefined' ? window.isTwoHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const beginDiscard = context.beginDiscard || (typeof window !== 'undefined' ? window.beginDiscard : () => {});
    const cpuDoPickYourCrib = context.cpuDoPickYourCrib || (typeof window !== 'undefined' ? window.cpuDoPickYourCrib : GameEngine.cpuDoPickYourCrib);

    if (settings.optCanadianDoubles && (parseInt(settings.playerCount, 10) || 2) === 4) {
      GameEngine.startCanadianDoublesFlow(context);
      return;
    }

    if (settings.optDrawCribbage && (parseInt(settings.playerCount, 10) || 2) === 2) {
      GameEngine.startDrawCribbagePhase(1, context);
      return;
    }

    if (settings.optPickYourCrib) {
      if (state && isSeatCpu(state.dealer)) {
        cpuDoPickYourCrib(context);
        return;
      }
      if (state && isTwoHuman() && state.dealer === 'p2') {
        passTo('p2', () => {
          state.currentHumanDiscard = 'p2';
          beginDiscard();
        });
        return;
      }
    }

    if ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && (parseInt(settings.playerCount, 10) || 2) === 2) {
      const pone = (state && state.dealer === 'p1') ? 'p2' : 'p1';
      const dealer = state ? state.dealer : 'p2';
      const poneDiscardN = settings.deal === 'super' ? 3 : 2;
      const dealerDiscardN = settings.deal === 'super' ? 6 : 4;

      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
      const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
      const chooseDiscard = context.chooseDiscard || (typeof window !== 'undefined' ? window.chooseDiscard : () => []);
      const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
      const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
      const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
      const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
      const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

      if (isSeatCpu(pone)) {
        // Pone is CPU: discards directly to dealer's hand
        const pHand = (getSeatHand(pone) || []).slice();
        const pThrow = chooseDiscard(pHand, false, settings.goal === 'low', poneDiscardN);
        const pThrowIds = new Set(pThrow.map(c => c.id));
        const pKept = pHand.filter(c => !pThrowIds.has(c.id));
        setSeatHand(pone, pKept);
        setSeatKept(pone, pKept.slice());
        state.seatDiscards = state.seatDiscards || {};
        state.seatDiscards[pone] = pThrow;

        const dHand = (getSeatHand(dealer) || []).slice();
        dHand.push(...pThrow);
        setSeatHand(dealer, dHand);
        log(`${seatName(pone)} discarded ${poneDiscardN} card${poneDiscardN === 1 ? '' : 's'} to ${seatName(dealer)}'s hand.`);

        if (isSeatCpu(dealer)) {
          // Dealer is also CPU (CPU vs CPU)
          const dThrow = chooseDiscard(dHand, true, settings.goal === 'low', dealerDiscardN);
          const dThrowIds = new Set(dThrow.map(c => c.id));
          const dKept = dHand.filter(c => !dThrowIds.has(c.id));
          setSeatHand(dealer, dKept);
          setSeatKept(dealer, dKept.slice());
          state.seatDiscards[dealer] = dThrow;
          state.crib = state.crib || [];
          state.crib.push(...dThrow);
          log(`${seatName(dealer)} discarded ${dealerDiscardN} cards to the crib.`);

          state.currentHumanDiscard = null;
          state.discardsComplete = true;
          if (settings.optCutBeforeDiscard) {
            startPlayAfterDiscards(context);
          } else {
            startStarter(context);
          }
          return;
        }

        // Dealer is human
        fancyCall(`${seatName(pone)} passed ${poneDiscardN} cards to your hand!`, 'combo', 1600);
        state.currentHumanDiscard = dealer;
        beginDiscard(context);
        return;
      }

      // Pone is human
      if (pone === 'p2' && isTwoHuman()) {
        passTo('p2', () => {
          state.currentHumanDiscard = 'p2';
          beginDiscard(context);
        });
        return;
      }
      state.currentHumanDiscard = pone;
      beginDiscard(context);
      return;
    }
    beginDiscard();
  }

  /**
   * Orchestrates the 10-card organization phase for Canadian Doubles.
   * Pone organizes first, then Dealer organizes.
   * When both have organized (4 keep, 4 pass, 2 crib), discards are marked complete.
   * @param {Object} [context]
   */
  static startCanadianDoublesFlow(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isDealerTeam = context.isDealerTeam || (typeof window !== 'undefined' ? window.isDealerTeam : () => false);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : s => s === 'p1' ? 'p2' : 'p1');
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);
    const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);

    const pone = nextSeat(state.dealer);
    const dealer = state.dealer;
    const getPartnerSeat = (s) => (s === 'p1' ? 'p3' : (s === 'p2' ? 'p4' : (s === 'p3' ? 'p1' : 'p2')));

    state.seatDiscards = state.seatDiscards || {};
    state.crib = state.crib || [];

    const poneDone = Boolean(state.seatDiscards[pone]);
    const dealerDone = Boolean(state.seatDiscards[dealer]);

    // Check if both have completed hand distribution
    if (poneDone && dealerDone) {
      state.currentHumanDiscard = null;
      state.discardsComplete = true;
      if (settings.optCutBeforeDiscard) {
        startPlayAfterDiscards(context);
      } else if (settings.optGuessStarter) {
        beginCutStarter(context);
      } else {
        startStarter(context);
      }
      return;
    }

    const ModalCls = CanadianDoublesModal || (typeof CC !== 'undefined' ? CC.CanadianDoublesModal : (typeof window !== 'undefined' ? (window.CanadianDoublesModal || window.CrazyCribbage?.CanadianDoublesModal) : null));

    // Step 1: Pone organizes first
    if (!poneDone) {
      const ponePartner = getPartnerSeat(pone);
      if (isSeatCpu(pone)) {
        const pHand = (getSeatHand(pone) || []).slice();
        const isD = isDealerTeam(pone);
        const opt = CribbageAI.organizeCanadianDoubles(pHand, isD, { settings, starter: state.starter });
        setSeatHand(pone, opt.keepHand);
        setSeatKept(pone, opt.keepHand.slice());
        setSeatHand(ponePartner, opt.partnerHand);
        setSeatKept(ponePartner, opt.partnerHand.slice());
        state.crib.push(...opt.cribDiscards);
        state.seatDiscards[pone] = opt.cribDiscards;
        log(`${seatName(pone)} organized hand: kept 4, passed 4 to ${seatName(ponePartner)}, discarded 2 to the crib.`);
        GameEngine.startCanadianDoublesFlow(context);
        return;
      } else {
        state.currentHumanDiscard = pone;
        state.phase = 'discard';
        setStatus(`${seatName(pone)}: Organize your 10 cards (Keep 4, Pass 4 to ${seatName(ponePartner)}, Discard 2).`);
        render();
        if (ModalCls && typeof ModalCls.open === 'function') {
          ModalCls.open(pone, { ...context, onComplete: () => GameEngine.startCanadianDoublesFlow(context) });
        }
        return;
      }
    }

    // Step 2: Dealer organizes second
    if (!dealerDone) {
      const dealerPartner = getPartnerSeat(dealer);
      if (isSeatCpu(dealer)) {
        const dHand = (getSeatHand(dealer) || []).slice();
        const isD = isDealerTeam(dealer);
        const opt = CribbageAI.organizeCanadianDoubles(dHand, isD, { settings, starter: state.starter });
        setSeatHand(dealer, opt.keepHand);
        setSeatKept(dealer, opt.keepHand.slice());
        setSeatHand(dealerPartner, opt.partnerHand);
        setSeatKept(dealerPartner, opt.partnerHand.slice());
        state.crib.push(...opt.cribDiscards);
        state.seatDiscards[dealer] = opt.cribDiscards;
        log(`${seatName(dealer)} organized hand: kept 4, passed 4 to ${seatName(dealerPartner)}, discarded 2 to the crib.`);
        GameEngine.startCanadianDoublesFlow(context);
        return;
      } else {
        hideWait();
        passTo(dealer, () => {
          state.currentHumanDiscard = dealer;
          state.phase = 'discard';
          setStatus(`${seatName(dealer)}: Organize your 10 cards (Keep 4, Pass 4 to ${seatName(dealerPartner)}, Discard 2).`);
          render();
          if (ModalCls && typeof ModalCls.open === 'function') {
            ModalCls.open(dealer, { ...context, onComplete: () => GameEngine.startCanadianDoublesFlow(context) });
          }
        });
        return;
      }
    }
  }

  /**
   * Authoritatively confirms and processes human discards across all match formats.
   * @param {Object} [context]
   */
  static confirmDiscard(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const confirmPickCrib = context.confirmPickCrib || (typeof window !== 'undefined' ? window.confirmPickCrib : GameEngine.confirmPickCrib);
    if (state.phase === 'pick-crib') {
      confirmPickCrib(context);
      return;
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const count = parseInt(settings.playerCount, 10) || 2;
    const currentSeat = state.currentHumanDiscard || 'p1';
    if (settings.optCanadianDoubles && count === 4) {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const curHand = getSeatHand(currentSeat);
      if (curHand && curHand.length === 10) {
        const ModalCls = CanadianDoublesModal || (typeof CC !== 'undefined' ? CC.CanadianDoublesModal : (typeof window !== 'undefined' ? (window.CanadianDoublesModal || window.CrazyCribbage?.CanadianDoublesModal) : null));
        if (ModalCls && typeof ModalCls.open === 'function') {
          ModalCls.open(currentSeat, { ...context, onComplete: () => GameEngine.startCanadianDoublesFlow(context) });
          return;
        }
      }
    }
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    let discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings.deal === 'super' ? 3 : 2));
    if ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      discardN = (currentSeat === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const me = mySeat();

    if (!state.selected || state.selected.length !== discardN) return;

    const seatDiscardsToDeck = context.seatDiscardsToDeck || (typeof window !== 'undefined' ? window.seatDiscardsToDeck : () => false);
    if (settings && settings.optFivesCribbage && !seatDiscardsToDeck(currentSeat)) {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0);
      const currHand = getSeatHand(currentSeat) || [];
      const fivesInHand = currHand.filter(c => rankOf(c) === 5);
      const reqFives = Math.min(fivesInHand.length, discardN);
      if (reqFives > 0) {
        const selIds = new Set(state.selected);
        const selectedFives = currHand.filter(c => selIds.has(c.id) && rankOf(c) === 5);
        if (selectedFives.length < reqFives) {
          const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
          const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
          fancyCall("Five's Cribbage: Discard your 5s!", 'go', 1600);
          setStatus(`Five's Cribbage: You must discard any 5s in your hand to the crib (${reqFives} required).`);
          return;
        }
      }
    }

    if (settings && settings.optSevensAndEightsCribbage && !seatDiscardsToDeck(currentSeat)) {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0);
      const currHand = getSeatHand(currentSeat) || [];
      const sevensAndEights = currHand.filter(c => {
        const r = rankOf(c);
        return r === 7 || r === 8;
      });
      const reqCount = Math.min(sevensAndEights.length, discardN);
      if (reqCount > 0) {
        const selIds = new Set(state.selected);
        const selectedTarget = currHand.filter(c => selIds.has(c.id) && (rankOf(c) === 7 || rankOf(c) === 8));
        if (selectedTarget.length < reqCount) {
          const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
          const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
          fancyCall("Sevens and Eights Cribbage: Discard your 7s and 8s!", 'go', 1600);
          setStatus(`Sevens and Eights Cribbage: You must discard any 7s and 8s in your hand to the crib (${reqCount} required).`);
          return;
        }
      }
    }

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const handleSeatDiscardIntent = context.handleSeatDiscardIntent || (typeof window !== 'undefined' ? window.handleSeatDiscardIntent : () => {});

    if (isOnline()) {
      if (settings.netRole === 'guest') {
        netSend({ type: 'intent', op: 'discard', ids: state.selected, seatId: me });
        state.selected = [];
        setStatus('Discards submitted. Waiting for other players…');
        render();
        return;
      } else {
        handleSeatDiscardIntent(me, state.selected);
        return;
      }
    }

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const startHumanPickCrib = context.startHumanPickCrib || (typeof window !== 'undefined' ? window.startHumanPickCrib : GameEngine.startHumanPickCrib);
    const isTwoHuman = context.isTwoHuman || (typeof window !== 'undefined' ? window.isTwoHuman : () => false);
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isDealerTeam = context.isDealerTeam || (typeof window !== 'undefined' ? window.isDealerTeam : () => false);
    const chooseDiscard = context.chooseDiscard || (typeof window !== 'undefined' ? window.chooseDiscard : () => []);
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : GameEngine.startPlayAfterDiscards);
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : GameEngine.startStarter);

    const ids = new Set(state.selected);
    const currHand = getSeatHand(currentSeat);
    const thrown = currHand.filter(c => ids.has(c.id));
    const remaining = currHand.filter(c => !ids.has(c.id));
    setSeatHand(currentSeat, remaining);
    setSeatKept(currentSeat, remaining.slice());
    state.seatDiscards = state.seatDiscards || {};
    state.seatDiscards[currentSeat] = thrown;
    state.selected = [];

    if (!state.coachHistory) {
      state.coachHistory = { discards: [], peggingTurns: [], cuts: [] };
    }
    if (currentSeat === 'p1' || currentSeat === 'you') {
      state.coachHistory.discards.push({
        round: state.round || 1,
        isDealer: Boolean(currentSeat === state.dealer || (typeof isDealerTeam === 'function' && isDealerTeam(currentSeat))),
        hand: currHand.slice(),
        discards: thrown.slice(),
        kept: remaining.slice()
      });
    }

    if ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      if (currentSeat !== state.dealer) {
        // Pone discards to dealer's hand
        const dealerSeat = state.dealer;
        const dHand = (getSeatHand(dealerSeat) || []).slice();
        dHand.push(...thrown);
        setSeatHand(dealerSeat, dHand);
        log(`${seatName(currentSeat)} discarded ${thrown.length} card${thrown.length > 1 ? 's' : ''} to ${seatName(dealerSeat)}'s hand.`);

        // Now dealer discards 4 (or 6 in super)
        const dealerDiscardN = settings.deal === 'super' ? 6 : 4;
        if (isSeatCpu(dealerSeat)) {
          const dCurrHand = getSeatHand(dealerSeat);
          const cpuThrow = chooseDiscard(dCurrHand, true, settings.goal === 'low', dealerDiscardN);
          const cids = new Set(cpuThrow.map(c => c.id));
          const dealerKept = dCurrHand.filter(c => !cids.has(c.id));
          setSeatHand(dealerSeat, dealerKept);
          setSeatKept(dealerSeat, dealerKept.slice());
          state.crib = state.crib || [];
          state.crib.push(...cpuThrow);
          state.seatDiscards = state.seatDiscards || {};
          state.seatDiscards[dealerSeat] = cpuThrow;
          log(`${seatName(dealerSeat)} discarded ${cpuThrow.length} cards to the crib.`);

          state.currentHumanDiscard = null;
          state.discardsComplete = true;
          if (settings.optCutBeforeDiscard) {
            startPlayAfterDiscards(context);
          } else {
            startStarter(context);
          }
          return;
        } else {
          // Dealer is human (pass & play)
          hideWait();
          passTo(dealerSeat, () => {
            state.currentHumanDiscard = dealerSeat;
            state.phase = 'discard';
            const beginDiscard = context.beginDiscard || (typeof window !== 'undefined' ? window.beginDiscard : GameEngine.beginDiscard);
            beginDiscard(context);
          });
          return;
        }
      } else {
        // Dealer discards to crib
        state.crib = state.crib || [];
        state.crib.push(...thrown);
        log(`${seatName(currentSeat)} discarded ${thrown.length} cards to the crib.`);

        state.currentHumanDiscard = null;
        state.discardsComplete = true;
        if (settings.optCutBeforeDiscard) {
          startPlayAfterDiscards(context);
        } else {
          startStarter(context);
        }
        return;
      }
    }

    if (seatDiscardsToDeck(currentSeat)) {
      state.deckDiscards = state.deckDiscards || [];
      state.deckDiscards.push(...thrown);
      log(`${seatName(currentSeat)} discards 1 card to the deck.`);
    } else {
      state.crib = state.crib || [];
      state.crib.push(...thrown);
    }

    if (settings.optPickYourCrib) {
      const pones = getActiveSeatIds().filter(sId => sId !== state.dealer);
      state.pickPones = pones.slice();
      startHumanPickCrib(context);
      return;
    }

    // For 2-player pass-and-play:
    if (count === 2 && isTwoHuman() && currentSeat === 'p1') {
      hideWait();
      passTo('p2', () => {
        state.currentHumanDiscard = 'p2';
        if (settings.jokers && (getSeatHand('p2') || []).some(c => isWild(c) && !c.playAs)) {
          state.phase = 'assign-p2';
          state.assignTarget = 'hand-p2';
          render();
          netPush();
          return;
        }
        state.phase = 'discard-p2';
        render();
        netPush({ fancy: { text: 'Your turn to discard!', kind: 'combo', ms: 1200 } });
      });
      return;
    }

    // Multi-player pass & play: check if another human seat needs to discard
    const pendingHuman = getActiveSeatIds().find(sId => isSeatHuman(sId) && (!state.seatDiscards || !state.seatDiscards[sId]));
    if (pendingHuman) {
      hideWait();
      passTo(pendingHuman, () => {
        state.currentHumanDiscard = pendingHuman;
        state.phase = 'discard';
        render();
      });
      return;
    }

    // Handle all other active seats:
    const activeSeats = getActiveSeatIds();
    for (const sId of activeSeats) {
      if (isSeatCpu(sId) && (!state.seatDiscards || !state.seatDiscards[sId])) {
        const sHand = getSeatHand(sId);
        const isDealer = isDealerTeam(sId);
        const toDeck = seatDiscardsToDeck(sId);
        const cpuThrow = chooseDiscard(sHand, isDealer, settings.goal === 'low', discardN, toDeck, sId);
        const cids = new Set(cpuThrow.map(c => c.id));
        const sKept = sHand.filter(c => !cids.has(c.id));
        setSeatHand(sId, sKept);
        setSeatKept(sId, sKept.slice());
        state.seatDiscards = state.seatDiscards || {};
        state.seatDiscards[sId] = cpuThrow;
        if (toDeck) {
          state.deckDiscards = state.deckDiscards || [];
          state.deckDiscards.push(...cpuThrow);
          log(`${seatName(sId)} discards 1 card to the deck.`);
        } else {
          state.crib = state.crib || [];
          state.crib.push(...cpuThrow);
        }
        try {
          GameReplayManager.recordEvent('discard', { who: sId, discards: cpuThrow });
        } catch (_) {}
      }
    }

    state.currentHumanDiscard = null;
    if (settings && settings.optDrawCribbage && count === 2) {
      GameEngine.startDrawCribbagePhase(2, context);
      return;
    }
    if (settings.optCutBeforeDiscard) {
      state.discardsComplete = true;
      startPlayAfterDiscards(context);
    } else if (settings.optGuessStarter) {
      state.discardsComplete = true;
      const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : GameEngine.beginCutStarter);
      beginCutStarter(context);
    } else {
      startStarter(context);
    }
  }

  /**
   * Determines which active players with remaining cards were unable to play
   * and were skipped between fromSeat and nextPlayer.
   * @param {string} fromSeat - The seat that just played
   * @param {string|null} nextPlayer - The next seat with a legal play, or null if no one can play
   * @param {Array<string>} activeSeats - Active seat IDs in rotation order
   * @param {Function} getSeatHand - Function returning remaining cards for a seat
   * @returns {Array<string>} Seat IDs that had to call 'Go'
   */
  static getSkippedGoSeats(fromSeat, nextPlayer, activeSeats, getSeatHand) {
    if (!Array.isArray(activeSeats) || activeSeats.length === 0) return [];
    const startIndex = activeSeats.indexOf(fromSeat);
    if (startIndex === -1) return [];
    const n = activeSeats.length;
    const skipped = [];

    const limit = (nextPlayer === null) ? n : (nextPlayer === fromSeat ? n : ((activeSeats.indexOf(nextPlayer) - startIndex + n) % n));

    for (let i = 1; i < limit; i++) {
      const idx = (startIndex + i) % n;
      const sId = activeSeats[idx];
      const hand = (typeof getSeatHand === 'function') ? getSeatHand(sId) : [];
      if (hand && hand.length > 0) {
        skipped.push(sId);
      }
    }
    return skipped;
  }

  /**
   * Displays an on-screen message and triggers notifications when a player says Go.
   * @param {string|Array<string>} whoSaidGo - Seat ID or array of Seat IDs who called Go
   * @param {Object} [context]
   */
  static announceGo(whoSaidGo, context = {}) {
    const rawSeats = Array.isArray(whoSaidGo) ? whoSaidGo : [whoSaidGo];
    const seats = rawSeats
      .map(s => (s && typeof s === 'object') ? (s.id || '') : s)
      .filter(s => typeof s === 'string' && s.length > 0 && !s.includes('[object'));
    if (seats.length === 0) return;

    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    const me = mySeat();
    const names = seats.map(s => {
      if (s === me) return 'You';
      const n = seatName(s);
      if (!n || typeof n !== 'string' || n.includes('[object')) {
        return (s === 'p1' ? 'You' : 'Opponent');
      }
      return (n === 'You' || (typeof n === 'string' && n.endsWith('(You)'))) ? 'You' : n;
    });

    let displayMsg = '';
    if (names.length === 1) {
      displayMsg = (names[0] === 'You') ? 'You say "Go!"' : `${names[0]} says "Go!"`;
    } else if (names.length === 2) {
      displayMsg = `${names[0]} & ${names[1]} say "Go!"`;
    } else {
      displayMsg = `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]} say "Go!"`;
    }

    // Center screen banner
    fancyCall(displayMsg, 'go', 1800);

    // Turn prompt / status update
    setStatus(displayMsg);

    // Log who said go
    seats.forEach(s => {
      const sN = seatName(s);
      log(`${sN} says go.`);
    });

    // 3D avatar reaction bubble
    try {
      const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
      if (renderer3D && typeof renderer3D.showSeatReaction === 'function') {
        seats.forEach(s => renderer3D.showSeatReaction(s, '💬 GO!'));
      }
    } catch (_) {}

    // Synchronize to remote human players in online games
    netPush({
      fancy: {
        text: displayMsg,
        kind: 'go',
        ms: 1800,
        goSeat: seats[0],
        goSeats: seats
      }
    });
  }

  /**
   * Displays an on-screen message and updates prompts when it is a human player's turn to play a card.
   * @param {string} seatId
   * @param {Object} [context]
   */
  static announcePlayTurn(seatId, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.over || state.phase !== 'play') return;

    const seat = seatId || state.turn;
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    if (!isSeatHuman(seat)) return;

    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    const me = mySeat();
    const isMe = (seat === me) || (!isOnline() && (seat === 'p1' || seat === 'you'));
    const msg = isMe
      ? 'Your turn: Play a card'
      : `${seatName(seat)}’s turn: Play a card`;

    fancyCall(msg, 'play-turn', 1500);
    setStatus(msg);

    try {
      const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
      if (renderer3D && typeof renderer3D.showSeatReaction === 'function') {
        renderer3D.showSeatReaction(seat, '🎴');
      }
    } catch (_) {}

    if (isOnline()) {
      netPush({
        fancy: {
          text: msg,
          kind: 'play-turn',
          ms: 1500,
          turnSeat: seat
        }
      });
    }
  }

  /**
   * Settles a 'Go' or '31' event, resets running count/pile, and advances lead.
   * @param {string} whoJustPlayed
   * @param {Object} [context]
   * @param {Array<string>|string} [goSeats=null]
   */
  static settleGo(whoJustPlayed, arg2 = {}, arg3 = null) {
    let context = {};
    let goSeats = null;

    if (Array.isArray(arg2) || typeof arg2 === 'string') {
      goSeats = arg2;
      context = (arg3 && typeof arg3 === 'object' && !Array.isArray(arg3)) ? arg3 : {};
    } else if (arg2 && typeof arg2 === 'object' && !Array.isArray(arg2)) {
      context = arg2;
      goSeats = (Array.isArray(arg3) || typeof arg3 === 'string') ? arg3 : null;
    } else {
      goSeats = (Array.isArray(arg3) || typeof arg3 === 'string') ? arg3 : null;
    }

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const finishPlay = context.finishPlay || (typeof window !== 'undefined' ? window.finishPlay : GameEngine.finishPlay);
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const nextSeat = context.nextSeat
      || (typeof window !== 'undefined' && typeof window.nextSeat === 'function' ? window.nextSeat : (s) => GameEngine.advanceDealer(s, getActiveSeatIds()));
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});

    const is31 = state.count === 31;
    state.turn = null;
    state.frozen = true;
    state.pileFaceDown = true;
    if (state.pile && state.pile.length) {
      state.pile.forEach(c => {
        if (c) c.faceDown = true;
      });
    }
    if (state.played && state.played.length) {
      state.played.forEach(item => {
        if (item && item.card) item.card.faceDown = true;
        if (item) item.faceDown = true;
      });
    }

    let seatsWhoSaidGo = null;
    if (Array.isArray(goSeats)) {
      const filtered = goSeats
        .map(s => (s && typeof s === 'object') ? (s.id || '') : s)
        .filter(s => typeof s === 'string' && s.length > 0 && !s.includes('[object'));
      if (filtered.length > 0) seatsWhoSaidGo = filtered;
    } else if (typeof goSeats === 'string' && goSeats.length > 0 && !goSeats.includes('[object')) {
      seatsWhoSaidGo = [goSeats];
    }

    const cleanWhoJustPlayed = (typeof whoJustPlayed === 'string')
      ? whoJustPlayed
      : ((whoJustPlayed && typeof whoJustPlayed === 'object' && whoJustPlayed.id) ? whoJustPlayed.id : 'p1');

    if (!seatsWhoSaidGo || seatsWhoSaidGo.length === 0) {
      const activeSeats = getActiveSeatIds().filter(sId => typeof sId === 'string' && sId !== cleanWhoJustPlayed && !sId.includes('[object'));
      seatsWhoSaidGo = activeSeats.filter(sId => getSeatHand(sId).length > 0);
    }

    const me = mySeat();
    let goText = 'GO';

    if (is31) {
      goText = 'Thirty-one!';
    } else if (seatsWhoSaidGo && seatsWhoSaidGo.length > 0) {
      const names = seatsWhoSaidGo.map(s => {
        if (s === me) return 'You';
        const n = seatName(s);
        if (!n || typeof n !== 'string' || n.includes('[object')) {
          return (s === 'p1' ? 'You' : 'Opponent');
        }
        return (n === 'You' || (typeof n === 'string' && n.endsWith('(You)'))) ? 'You' : n;
      });
      let whoSays = '';
      if (names.length === 1) {
        whoSays = (names[0] === 'You') ? 'You say "Go!"' : `${names[0]} says "Go!"`;
      } else if (names.length === 2) {
        whoSays = `${names[0]} & ${names[1]} say "Go!"`;
      } else {
        whoSays = `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]} say "Go!"`;
      }
      const rawScorer = (cleanWhoJustPlayed === me) ? 'You' : seatName(cleanWhoJustPlayed);
      const scorer = (typeof rawScorer === 'string' && !rawScorer.includes('[object'))
        ? rawScorer
        : (cleanWhoJustPlayed === 'p1' ? 'You' : 'Opponent');
      goText = `${whoSays} — 1 pt to ${scorer}`;

      // Log who said go
      seatsWhoSaidGo.forEach(s => {
        const sN = seatName(s);
        log(`${sN} says go.`);
      });

      // 3D reaction bubble above avatar
      try {
        const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
        if (renderer3D && typeof renderer3D.showSeatReaction === 'function') {
          seatsWhoSaidGo.forEach(s => renderer3D.showSeatReaction(s, '💬 GO!'));
        }
      } catch (_) {}
    } else {
      const scorer = (whoJustPlayed === me) ? 'You' : seatName(whoJustPlayed);
      goText = `Go / Last Card — 1 pt to ${scorer}`;
    }

    try {
      GameReplayManager.recordEvent('peg_go', {
        who: whoJustPlayed,
        points: is31 ? 2 : 1,
        reason: is31 ? '31' : 'Go',
        scores: state.scores
      });
    } catch (_) {}
    fancyCall(goText, 'go', 1800);
    setStatus(goText);

    // Turn 2D played cards face down immediately
    if (doc) {
      const pl = doc.getElementById('playedCards');
      if (pl) {
        pl.classList.add('face-down');
        const cards = pl.querySelectorAll('.playing-card');
        cards.forEach((cEl) => {
          cEl.classList.add('back', 'face-down', 'flipping-down');
          cEl.removeAttribute('data-badge');
          cEl.innerHTML = '<span></span><span class="suit">✦</span>';
        });
      }
    }

    // Turn 3D played cards face down immediately
    try {
      const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
      if (renderer3D && renderer3D.cardEngine && typeof renderer3D.cardEngine.turnPlayPileFaceDown === 'function') {
        renderer3D.cardEngine.turnPlayPileFaceDown();
      }
    } catch (_) {}

    render();
    netPush({
      fancy: {
        text: goText,
        kind: 'go',
        ms: 1800,
        goSeat: (seatsWhoSaidGo && seatsWhoSaidGo[0]) || null,
        goSeats: seatsWhoSaidGo,
        scorerSeat: whoJustPlayed
      },
      sfx: is31 ? 'bell31' : 'peg'
    });

    setTimeout(() => {
      if (doc) {
        const pl = doc.getElementById('playedCards');
        if (pl) pl.classList.add('sweeping');
      }
      try {
        const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
        if (renderer3D && renderer3D.cardEngine && typeof renderer3D.cardEngine.sweepPlayPile === 'function') {
          renderer3D.cardEngine.sweepPlayPile();
        }
      } catch (_) {}
    }, 1400);

    setTimeout(() => {
      state.frozen = false;
      state.pileFaceDown = false;
      if (doc) {
        const pl = doc.getElementById('playedCards');
        if (pl) pl.classList.remove('face-down', 'sweeping');
      }
      if (state.over || state.phase !== 'play') return;

      const activeSeats = getActiveSeatIds();
      const anyCardsLeft = activeSeats.some(sId => getSeatHand(sId).length > 0);
      if (!anyCardsLeft) {
        finishPlay(context);
        return;
      }

      const cleanWhoJustPlayed = (typeof whoJustPlayed === 'string')
        ? whoJustPlayed
        : ((whoJustPlayed && typeof whoJustPlayed === 'object' && whoJustPlayed.id) ? whoJustPlayed.id : 'p1');
      let nextLead = nextSeat(cleanWhoJustPlayed);
      const startLead = nextLead;
      while (getSeatHand(nextLead).length === 0) {
        nextLead = nextSeat(nextLead);
        if (nextLead === startLead) break;
      }

      state.count = 0;
      state.pile = [];
      state.turn = nextLead;
      render();
      netPush();

      if (isSeatCpu(nextLead)) {
        scheduleOppPlay(nextLead);
      } else if (isSeatHuman(nextLead)) {
        GameEngine.announcePlayTurn(nextLead, context);
        if (!isOnline() && nextLead !== whoJustPlayed) {
          passTo(nextLead, () => {
            render();
          });
        }
      }
    }, 2000);
  }

  /**
   * Plays a card to the pegging pile, validates counts, scores pegging, and routes turn.
   * @param {string} who
   * @param {Object} card
   * @param {Object} [context]
   */
  static playCard(who, card, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || !card || state.over || state.frozen || state.paused || state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus) return;
    if (state.phase && state.phase !== 'play' && state.phase !== 'pegging') return;
    if (!who) return;

    let sId = who;
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';

    if (state.turn === null) return;
    if (state.turn !== undefined) {
      let turnSeat = state.turn;
      if (turnSeat === 'you') turnSeat = 'p1';
      if (turnSeat === 'cpu') turnSeat = 'p2';
      if (turnSeat !== sId) return;
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const VAL = context.VAL || (typeof window !== 'undefined' ? window.VAL : (r) => Math.min(r, 10));
    const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => c.rank);
    const pegPoints = context.pegPoints || (typeof window !== 'undefined' ? window.pegPoints : () => []);
    const isBonusMessage = context.isBonusMessage || (typeof window !== 'undefined' ? window.isBonusMessage : () => false);
    const announceCombos = context.announceCombos || (typeof window !== 'undefined' ? window.announceCombos : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => String(c.rank));
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : () => false);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : { snap: () => {}, chime15: () => {}, bell31: () => {} });
    const addScore = context.addScore || (typeof window !== 'undefined' ? window.addScore : () => {});
    const fancyPegPhrase = context.fancyPegPhrase || (typeof window !== 'undefined' ? window.fancyPegPhrase : (w, n) => `${w} +${n}`);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const settleGo = context.settleGo || (typeof window !== 'undefined' ? window.settleGo : GameEngine.settleGo);
    const findNextPlayerWithPlay = context.findNextPlayerWithPlay || (typeof window !== 'undefined' ? window.findNextPlayerWithPlay : GameEngine.findNextPlayerWithPlay);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const getSeatKept = context.getSeatKept || (typeof window !== 'undefined' ? window.getSeatKept : null);
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : null);

    if ((state.count || 0) + VAL(rankOf(card)) > 31) return;

    const hand = getSeatHand(sId);
    const idx = hand.findIndex(c => c.id === card.id);
    if (idx < 0) return;

    let maxPegPts = 0;
    try {
      const currentCountBefore = state.count || 0;
      const legalCandidateCards = hand.filter(c => (currentCountBefore + VAL(rankOf(c))) <= 31);
      for (const candidate of legalCandidateCards) {
        const testPile = (state.pile || []).concat(candidate);
        const testRaw = pegPoints(testPile, candidate);
        const testTotal = testRaw.reduce((s, p) => s + p.n, 0);
        if (testTotal > maxPegPts) maxPegPts = testTotal;
      }
    } catch (_) {}

    hand.splice(idx, 1);
    if (sId === 'p1') state.youHand = hand;
    if (sId === 'p2') state.cpuHand = hand;

    card.who = sId;
    card.owner = sId;
    state.pile = state.pile || [];
    state.played = state.played || [];
    state.pile.push(card);
    state.played.push({ who: sId, card });
    state.count = (state.count || 0) + VAL(rankOf(card));
    state.lastPlayed = sId;

    const isSpadesCut = Boolean(settings && settings.optSpades && (state.spadesCut || (state.starters && state.starters.some(isSpadeCard))));
    const rawPts = pegPoints(state.pile, card);
    const pts = isSpadesCut ? rawPts.map(p => ({ ...p, n: p.n * 2, why: `${p.why} (spades x2)` })) : rawPts;
    const total = pts.reduce((s, p) => s + p.n, 0);
    const why = pts.map(p => p.why).join(', ') || 'play';
    const isBonusPeg = pts.some(p => isBonusMessage(p.why)) || (state.count === 31 && settings && settings.opt31sFor5);
    if (total) {
      card.pegBadge = '+' + total;
      if (isBonusPeg) card.pegBadgeBonus = true;
    }
    if (state.count === 31 && !card.pegBadge) {
      card.pegBadge = '31';
      if (settings && settings.opt31sFor5) card.pegBadgeBonus = true;
    }
    if (pts.length) announceCombos(pts);

    let progStealPhrase = null;
    if (settings && settings.optProgressiveSteals && state.pile.length >= 2) {
      const lastR = rankOf(card);
      let same = 0;
      for (let i = state.pile.length - 1; i >= 0; i--) {
        if (rankOf(state.pile[i]) === lastR) same++;
        else break;
      }
      if (same >= 2 && state.played.length >= same) {
        const pairedPlays = state.played.slice(state.played.length - same);
        const prevPlay = pairedPlays[pairedPlays.length - 2];
        if (prevPlay && prevPlay.who !== sId) {
          const cardsToControl = pairedPlays.map(p => p.card);
          const activeSeats = (typeof context.getActiveSeatIds === 'function')
            ? context.getActiveSeatIds()
            : (context.activeSeats || (state.seats ? state.seats.map(s => s.id) : ['p1', 'p2']));

          const cardMatches = (a, b) => {
            if (!a || !b) return false;
            if (a.id && b.id) return a.id === b.id;
            return a.rank === b.rank && a.suit === b.suit;
          };

          const getKeptCards = (seat) => {
            if (typeof getSeatKept === 'function') {
              const res = getSeatKept(seat, state) || getSeatKept(seat);
              if (Array.isArray(res)) return res;
            }
            return GameState.getSeatKept(seat, state) || [];
          };

          const setKeptCards = (seat, cards) => {
            if (typeof setSeatKept === 'function') {
              setSeatKept(seat, cards);
            }
            GameState.setSeatKept(seat, cards, state);
          };

          // Remove controlled cards from all other players' kept hands
          for (const otherSeat of activeSeats) {
            if (otherSeat === sId) continue;
            const otherKept = getKeptCards(otherSeat).slice();
            const filtered = otherKept.filter(c => !cardsToControl.some(sc => cardMatches(sc, c)));
            if (filtered.length !== otherKept.length) {
              setKeptCards(otherSeat, filtered);
            }
          }

          // Add controlled cards to current player's kept hand
          const myKept = getKeptCards(sId).slice();
          for (const c of cardsToControl) {
            if (!myKept.some(existing => cardMatches(existing, c))) {
              myKept.push(c);
            }
          }
          setKeptCards(sId, myKept);

          progStealPhrase = same === 2
            ? `${seatName(sId)} stole ${cardLabel(prevPlay.card)}!`
            : (same === 3
                ? `${seatName(sId)} controls all 3 cards!`
                : `${seatName(sId)} controls all 4 cards!`);
          card.pegBadgeBonus = true;
          fancyCall(progStealPhrase, 'combo', 1800);
          log(`⚡ PROGRESSIVE STEAL: ${progStealPhrase}`);
        }
      }
    }

    const logMsg = `${seatName(sId)} play ${cardLabel(card)}${isWild(card) ? ' (wild)' : ''}  (${state.count})` + (total ? ` +${total}` : '');
    log(logMsg);
    if (sfx.snap) sfx.snap();

    try {
      GameReplayManager.recordEvent('peg_play', {
        who: sId,
        card,
        countAfter: state.count,
        points: total,
        reason: why,
        scores: state.scores
      });

      if (!state.coachHistory) {
        state.coachHistory = { discards: [], peggingTurns: [], cuts: [] };
      }
      if (sId === 'p1' || sId === 'you') {
        state.coachHistory.peggingTurns.push({
          who: sId,
          round: state.round || 1,
          cardPlayed: card,
          pointsScored: total,
          maxPointsPossible: Math.max(total, maxPegPts),
          reason: why
        });
      }
    } catch (_) {}

    let soundName = 'snap';
    if (state.count === 15) { if (sfx.chime15) sfx.chime15(); soundName = 'chime15'; }
    if (state.count === 19 && settings && settings.opt19sFor2) { if (sfx.chime15) sfx.chime15(); soundName = 'chime15'; }
    if (state.count === 31) { if (sfx.bell31) sfx.bell31(); soundName = 'bell31'; }
    if (total) addScore(sId, total, why);

    const isFourOfAKind = pts.some(p => /double pair royal|4 of a kind|four of a kind/i.test(p.why || ''));
    if (isFourOfAKind && typeof window !== 'undefined' && window._currentRenderer3D) {
      window._currentRenderer3D.triggerActionCam({ type: 'four_of_a_kind', who: sId });
    }

    const isTopBonus = pts.length ? isBonusMessage(pts[0].why) : false;
    const fancyItem = progStealPhrase
      ? { text: progStealPhrase, kind: 'combo', ms: 2000 }
      : (pts.length
          ? { text: fancyPegPhrase(pts[0].why, pts[0].n), kind: 'combo', ms: isTopBonus ? 2200 : 1400 }
          : (state.count === 31 ? { text: (settings && settings.opt31sFor5) ? 'Thirty-one for five!' : 'Thirty-one!', kind: 'go', ms: (settings && settings.opt31sFor5) ? 2400 : 1400 } : null));

    const continuePlay = () => {
      if (state.over) {
        render();
        netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
        return;
      }

      if (state.count === 31) {
        netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
        settleGo(sId, context);
        return;
      }

      const activeSeats = (typeof context.getActiveSeatIds === 'function') ? context.getActiveSeatIds() : (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function' ? window.getActiveSeatIds() : ['p1', 'p2']);
      const nextPlayer = findNextPlayerWithPlay(sId, state.count);
      const skippedGoSeats = GameEngine.getSkippedGoSeats(sId, nextPlayer, activeSeats, getSeatHand);

      if (!nextPlayer) {
        const goPts = isSpadesCut ? 2 : 1;
        const goWhy = isSpadesCut ? 'go / last card (spades x2)' : 'go / last card';
        addScore(sId, goPts, goWhy);
        const last = state.pile[state.pile.length - 1];
        if (last) last.pegBadge = last.pegBadge ? last.pegBadge + (isSpadesCut ? ' GO (+2)' : ' GO') : (isSpadesCut ? 'GO (+2)' : 'GO');
        if (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused) {
          const resumeCb = () => {
            if (state.over) {
              render();
              netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
              return;
            }
            netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
            settleGo(sId, context, skippedGoSeats);
          };
          state._resumeAfterSign = resumeCb;
          state._resumeAfterAdultTimer = resumeCb;
          state._resumeAfterPintsBonus = resumeCb;
          return;
        }
        if (state.over) {
          render();
          netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
          return;
        }
        netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });
        settleGo(sId, context, skippedGoSeats);
        return;
      }

      if (skippedGoSeats.length > 0) {
        GameEngine.announceGo(skippedGoSeats, context);
      }

      state.turn = nextPlayer;
      render();
      netPush({ log: logMsg, fancy: fancyItem, sfx: soundName });

      if (isSeatCpu(nextPlayer)) {
        scheduleOppPlay(nextPlayer);
      } else if (isSeatHuman(nextPlayer)) {
        const announceDelay = (skippedGoSeats.length > 0) ? 1400 : (fancyItem ? (fancyItem.ms || 1200) : 100);
        setTimeout(() => {
          if (!state || state.over || state.phase !== 'play' || (state.turn !== nextPlayer && state.passingTo !== nextPlayer)) return;
          GameEngine.announcePlayTurn(nextPlayer, context);
          if (!isOnline() && nextPlayer !== sId) {
            passTo(nextPlayer, () => {
              render();
            });
          }
        }, announceDelay);
      }
    };

    if (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused) {
      state._resumeAfterSign = continuePlay;
      state._resumeAfterAdultTimer = continuePlay;
      state._resumeAfterPintsBonus = continuePlay;
      return;
    }
    continuePlay();
  }

  /**
   * Executes CPU player turn during pegging play.
   * @param {string} seatId
   * @param {Object} [context]
   */
  static cpuSeatPlay(seatId, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.over || state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused || state.phase !== 'play' || state.turn !== seatId) return;

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const choosePlay = context.choosePlay || (typeof window !== 'undefined' ? window.choosePlay : () => null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const findNextPlayerWithPlay = context.findNextPlayerWithPlay || (typeof window !== 'undefined' ? window.findNextPlayerWithPlay : GameEngine.findNextPlayerWithPlay);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());
    const addScore = context.addScore || (typeof window !== 'undefined' ? window.addScore : () => {});
    const settleGo = context.settleGo || (typeof window !== 'undefined' ? window.settleGo : GameEngine.settleGo);
    const playCard = context.playCard || (typeof window !== 'undefined' ? window.playCard : GameEngine.playCard);

    const hand = getSeatHand(seatId);
    const card = choosePlay(hand, state.pile, state.count, true, settings.goal === 'low', seatId);
    if (!card) {
      GameEngine.announceGo(seatId, context);
      const nextPlayer = findNextPlayerWithPlay(seatId, state.count);
      if (nextPlayer) {
        state.turn = nextPlayer;
        render();
        netPush();
        if (isSeatCpu(nextPlayer)) {
          scheduleOppPlay(nextPlayer);
        } else if (isSeatHuman(nextPlayer)) {
          setTimeout(() => {
            if (!state || state.over || state.phase !== 'play' || (state.turn !== nextPlayer && state.passingTo !== nextPlayer)) return;
            GameEngine.announcePlayTurn(nextPlayer, context);
            if (!isOnline()) {
              passTo(nextPlayer, () => {
                render();
              });
            }
          }, 1400);
        }
      } else {
        if (state.count !== 31 && state.lastPlayed) {
          const isSpadesCut = Boolean(settings && settings.optSpades && (state.spadesCut || (state.starters && state.starters.some(isSpadeCard))));
          const goPts = isSpadesCut ? 2 : 1;
          const goWhy = isSpadesCut ? 'go / last card (spades x2)' : 'go / last card';
          addScore(state.lastPlayed, goPts, goWhy);
          const last = state.pile[state.pile.length - 1];
          if (last) last.pegBadge = last.pegBadge ? last.pegBadge + (isSpadesCut ? ' GO (+2)' : ' GO') : (isSpadesCut ? 'GO (+2)' : 'GO');
        }
        if (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused) {
          const resumeCb = () => {
            if (state.over) { render(); return; }
            settleGo(state.lastPlayed || seatId, context, [seatId]);
          };
          state._resumeAfterSign = resumeCb;
          state._resumeAfterAdultTimer = resumeCb;
          state._resumeAfterPintsBonus = resumeCb;
          return;
        }
        if (state.over) { render(); return; }
        settleGo(state.lastPlayed || seatId, context, [seatId]);
      }
      return;
    }
    playCard(seatId, card, context);
  }

  /**
   * Legacy CPU turn handler delegating to seat 'p2'.
   * @param {Object} [context]
   */
  static cpuTurn(context = {}) {
    GameEngine.cpuSeatPlay('p2', context);
  }

  /**
   * Finalizes the pegging play phase, prepares hands for show, and starts hand scoring.
   * @param {Object} [context]
   */
  static finishPlay(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getPlayerCount = context.getPlayerCount || (typeof window !== 'undefined' ? window.getPlayerCount : () => 2);
    const getSeatKept = context.getSeatKept || (typeof window !== 'undefined' ? window.getSeatKept : () => []);
    const scoreShow = context.scoreShow || (typeof window !== 'undefined' ? window.scoreShow : () => ({ total: 0 }));
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const openShowModal = context.openShowModal || (typeof window !== 'undefined' ? window.openShowModal : () => {});
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' ? window.beginShowAct : () => {});
    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => s);
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : null) || GameState.setSeatKept;
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});

    if (state.over) return;

    state.phase = 'show';
    const cribEl = (typeof document !== 'undefined') ? document.getElementById('cribCards') : null;
    if (cribEl) {
      cribEl.classList.remove('playing');
      cribEl.classList.add('counting');
    }
    state.revealCrib = false;
    state.highlightIds = [];
    const pegOnly = settings.target === 31;
    const count = getPlayerCount();

    if (count === 2) {
      const isP1Deal = state.dealer === 'p1' || state.dealer === 'you';
      const pone = isP1Deal ? 'p2' : 'p1';
      const deal = isP1Deal ? 'p1' : 'p2';
      const poneKept = getSeatKept(pone);
      let dealKept = getSeatKept(deal);
      const isShotgun = Boolean(settings && settings.optShotgunCribbage);
      const isTwisted = Boolean(settings && settings.optTwistedCribbage);
      const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
      const starterArg = (starters.length > 1) ? starters : state.starter;
      const poneScore = scoreShow(poneKept, starterArg, false);
      let dealScore;
      let cribScore;
      let cribCards = state.crib;

      state.twistedCribbageResolved = false;

      if (isTwisted) {
        // Twisted Cribbage: combine all 8 cards from dealer's kept cards + crib
        // Partition into best two 4-card hands maximizing total points
        // Flush rule: all 5 cards (4 in hand + cut card) must match suit to score flush in both hands
        const combined = [...dealKept, ...(state.crib || [])];
        state.twisted8Cards = [...combined];
        state.originalDealerKept = [...dealKept];
        state.originalCrib = [...(state.crib || [])];
        const Scorer = (typeof CC !== 'undefined' && CC?.CribbageScorer) || (typeof window !== 'undefined' ? window.CribbageScorer : null) || CribbageScorer;
        const bestSplit = Scorer && typeof Scorer.findOptimalTwistedSplit === 'function'
          ? Scorer.findOptimalTwistedSplit(combined, starterArg, settings)
          : null;
        if (bestSplit) {
          dealKept = bestSplit.hand1;
          cribCards = bestSplit.hand2;
          dealScore = bestSplit.score1;
          cribScore = bestSplit.score2;
          state.crib = cribCards;
          if (typeof setSeatKept === 'function') {
            setSeatKept(deal, dealKept, state);
          } else if (state.seatKept) {
            state.seatKept[deal] = dealKept;
          }
          log(`🌀 Twisted Cribbage: ${pName(deal)} combined 8 cards into two optimal hands (+${dealScore.total} & +${cribScore.total} = ${bestSplit.total} pts)!`);
        } else {
          dealScore = scoreShow(dealKept, starterArg, false);
          cribScore = scoreShow(state.crib, starterArg, true, false, { dealerHand: dealKept });
        }
      } else {
        dealScore = scoreShow(dealKept, starterArg, false);
        cribScore = scoreShow(state.crib, starterArg, true, false, { dealerHand: dealKept });
      }

      const cribSigned = (pegOnly || isShotgun) ? 0 : (settings.optRockCrib ? -cribScore.total : cribScore.total);
      state.lastShow = {
        pone: { title: pName(pone) + ' hand (pone)', cards: poneKept, starter: state.starter, starters, score: poneScore, signed: poneScore.total, who: pone },
        dealer: {
          title: isTwisted ? `${pName(deal)} hand (Twisted 1/2)` : `${pName(deal)} hand (dealer)`,
          cards: dealKept,
          starter: state.starter,
          starters,
          score: dealScore,
          signed: dealScore.total,
          who: deal
        },
        crib: {
          title: isShotgun ? 'Crib (Shotgun Cribbage)' : (isTwisted ? `Crib (Twisted 2/2)` : 'Crib'),
          cards: cribCards,
          starter: state.starter,
          starters,
          score: cribScore,
          signed: cribSigned,
          who: deal
        }
      };
      if (pegOnly) {
        openShowModal(true);
        return;
      }
      state.showQueue = ['pone', 'dealer', 'crib'];
      beginShowAct(0);
    } else {
      // 3 or 4 players: rotate starting with player to dealer's left, then clockwise, dealer last
      const rawDealer = state.dealer || 'p1';
      const dealerSeat = (rawDealer === 'you') ? 'p1' : ((rawDealer === 'cpu') ? 'p2' : rawDealer);
      const activeSeats = (typeof context.getActiveSeatIds === 'function')
        ? context.getActiveSeatIds()
        : (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function' ? window.getActiveSeatIds() : ['p1', 'p2', 'p3', 'p4']);

      const showOrder = [];
      const dIdx = activeSeats.indexOf(dealerSeat);
      if (dIdx !== -1) {
        for (let i = 1; i < activeSeats.length; i++) {
          showOrder.push(activeSeats[(dIdx + i) % activeSeats.length]);
        }
        showOrder.push(dealerSeat);
      } else {
        const nextSeatFn = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => GameEngine.advanceDealer(s, activeSeats));
        let curr = nextSeatFn(dealerSeat);
        let safety = 0;
        while (curr !== dealerSeat && safety < activeSeats.length + 2) {
          showOrder.push(curr);
          curr = nextSeatFn(curr);
          safety++;
        }
        showOrder.push(dealerSeat);
      }

      state.lastShow = {};
      state.showQueue = [];

      const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
      const starterArg = (starters.length > 1) ? starters : state.starter;

      for (const sId of showOrder) {
        const kept = getSeatKept(sId);
        const sScore = scoreShow(kept, starterArg, false);
        const isDealer = sId === dealerSeat;
        const title = seatName(sId) + ' hand' + (isDealer ? ' (dealer)' : ' (pone)');
        state.lastShow[sId] = {
          title,
          cards: kept,
          starter: state.starter,
          starters,
          score: sScore,
          signed: sScore.total,
          who: sId
        };
        state.showQueue.push(sId);
      }

      const isShotgun = Boolean(settings && settings.optShotgunCribbage);
      const cribScore = scoreShow(state.crib, starterArg, true, false, { dealerHand: getSeatKept(dealerSeat) });
      const cribSigned = (pegOnly || isShotgun) ? 0 : (settings.optRockCrib ? -cribScore.total : cribScore.total);
      const isSolo = isSoloVsPairs();
      const cribOwner = isSolo ? 'p1' : dealerSeat;
      const baseCribTitle = isSolo ? ('Crib (' + seatName('p1') + ')') : ('Crib (' + seatName(dealerSeat) + ')');
      const cribTitle = isShotgun ? `${baseCribTitle} [Shotgun Cribbage]` : baseCribTitle;
      state.lastShow.crib = {
        title: cribTitle,
        cards: state.crib,
        starter: state.starter,
        starters,
        score: cribScore,
        signed: cribSigned,
        who: cribOwner
      };
      state.showQueue.push('crib');

      if (pegOnly) {
        openShowModal(true);
        return;
      }
      beginShowAct(0);
    }
  }

  /**
   * Evaluates the Chutes and Ladders option at the end of hand counting.
   * If all active players/tracks are tied (and score > 0), all players go back to the start (hole 0).
   * @param {Object} state
   * @param {Object} settings
   * @param {Object} [context]
   * @returns {boolean} True if Chutes and Ladders triggered
   */
  static checkChutesAndLadders(state, settings = {}, context = {}) {
    if (!settings || !settings.optChutesAndLadders) return false;
    if (!state || !state.scores) return false;

    const getActiveTracks = context.getActiveTracks || (typeof window !== 'undefined' ? window.getActiveTracks : () => ['p1', 'p2']);
    const tracks = (typeof getActiveTracks === 'function') ? getActiveTracks() : ['p1', 'p2'];
    if (!tracks || tracks.length < 2) return false;

    const getTrackScore = (track) => {
      if (track === 'p1' && state.scores.p1 === undefined && state.scores.you !== undefined) return state.scores.you;
      if (track === 'p2' && state.scores.p2 === undefined && state.scores.cpu !== undefined) return state.scores.cpu;
      return state.scores[track] ?? 0;
    };

    const scores = tracks.map(getTrackScore);
    const firstScore = scores[0];
    const allTied = scores.every(s => s === firstScore);

    if (allTied && firstScore > 0) {
      state.chutesAndLaddersTriggered = true;
      state.chutesAndLaddersTiedScore = firstScore;

      // Reset all tracks back to start (hole 0)
      for (const t of tracks) {
        state.scores[t] = 0;
        if (state.prev) state.prev[t] = 0;
      }
      if (state.scores.you !== undefined) state.scores.you = 0;
      if (state.scores.cpu !== undefined) state.scores.cpu = 0;
      if (state.prev) {
        state.prev.you = 0;
        state.prev.cpu = 0;
      }

      if (context.pegTracker && typeof context.pegTracker.reset === 'function') {
        context.pegTracker.reset();
      } else if (typeof window !== 'undefined' && window.pegTracker && typeof window.pegTracker.reset === 'function') {
        window.pegTracker.reset();
      }

      if (typeof window !== 'undefined' && window.pegState) {
        for (const t of tracks) {
          if (window.pegState[t]) { window.pegState[t].front = 0; window.pegState[t].rear = 0; }
        }
        if (window.pegState.you) { window.pegState.you.front = 0; window.pegState.you.rear = 0; }
        if (window.pegState.cpu) { window.pegState.cpu.front = 0; window.pegState.cpu.rear = 0; }
      }
      if (context.pegState) {
        for (const t of tracks) {
          if (context.pegState[t]) { context.pegState[t].front = 0; context.pegState[t].rear = 0; }
        }
        if (context.pegState.you) { context.pegState.you.front = 0; context.pegState.you.rear = 0; }
        if (context.pegState.cpu) { context.pegState.cpu.front = 0; context.pegState.cpu.rear = 0; }
      }

      state.over = false;
      state.winner = null;

      const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (k => k));
      const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
      const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
      const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
      const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
      const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);

      const playerNames = tracks.map(t => pName(t)).join(' & ');
      log(`🎢 CHUTES AND LADDERS! All players (${playerNames}) are tied at ${firstScore} pts! Everyone slides back to the start (hole 0)!`);
      if (typeof fancyCall === 'function') {
        fancyCall(`Chutes & Ladders! Back to 0!`, 'famous', 3000);
      }
      if (sfx && typeof sfx.chutesAndLadders === 'function') {
        sfx.chutesAndLadders();
      } else if (sfx && typeof sfx.slide === 'function') {
        sfx.slide();
      } else if (sfx && typeof sfx.jail === 'function') {
        sfx.jail();
      }
      if (typeof renderScores === 'function') {
        renderScores(true);
      }
      if (typeof netPush === 'function') {
        netPush({
          fancy: { text: `Chutes & Ladders! Back to 0!`, kind: 'famous', ms: 3000 },
          sfx: 'chutesAndLadders'
        });
      }
      return true;
    }
    return false;
  }

  /**
   * Evaluates if match victory or defeat conditions are reached and displays results modal.
   * @param {Object} [context]
   * @returns {boolean}
   */
  static checkGameOver(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const state = context.state || (typeof window !== 'undefined' ? window.state : {});
    const getActiveTracks = context.getActiveTracks || (typeof window !== 'undefined' ? window.getActiveTracks : () => ['p1', 'p2']);
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (k => k));
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const recordGameResult = context.recordGameResult || (typeof window !== 'undefined' ? window.recordGameResult : () => ({ wins: 0, losses: 0 }));
    const renderRecordBox = context.renderRecordBox || (typeof window !== 'undefined' ? window.renderRecordBox : () => {});
    const skunkResult = context.skunkResult || (typeof window !== 'undefined' ? window.skunkResult : () => null);
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : () => {});

    const t = settings.target || 121;
    const tracks = getActiveTracks();
    let over = false, winner = null;

    const isProgCut = Boolean(settings && settings.optProgressiveCut && tracks.length === 2);
    if (isProgCut) {
      // In Progressive cut, game ends ONLY when at least one player has >121 points (or >= target)
      // AND both players have had the same number of cribs (even handsCompleted).
      // Mid-hand (play phase, uncompleted show) cannot end the game.
      if (state.phase === 'show' && state.showAct === 'done') {
        const handsDone = state.handsCompleted || 0;
        if (handsDone > 0 && handsDone % 2 === 0) {
          const s1 = state.scores ? (state.scores[tracks[0]] || 0) : 0;
          const s2 = state.scores ? (state.scores[tracks[1]] || 0) : 0;
          const maxScore = Math.max(s1, s2);
          if (maxScore > 121 || maxScore >= t) {
            // If both players are tied after even cribs, carry on to the next cut level!
            if (s1 !== s2) {
              over = true;
              winner = (settings.goal === 'low') ? (s1 < s2 ? tracks[0] : tracks[1]) : (s1 > s2 ? tracks[0] : tracks[1]);
            }
          }
        }
      }
    } else {
      if (settings.optChutesAndLadders) {
        GameEngine.checkChutesAndLadders(state, settings, context);
      }
      const res = GameEngine.checkMatchOver(state.scores, t, settings.goal || 'high', tracks);
      over = res.over;
      winner = res.winner;
    }

    if (over) {
      state.over = true;
      state.showModal = null;
      state.showMeter = null;
      if (typeof document !== 'undefined') {
        const sm = document.getElementById('showMeter');
        if (sm) sm.classList.add('hidden');
        const muggins = document.getElementById('mugginsPromptWrap');
        if (muggins) muggins.classList.add('hidden');
      }
      if (sfx && typeof sfx.gameHole === 'function') sfx.gameHole();

      const scoreSummary = tracks.map(k => `${pName(k)}: ${state.scores[k] || 0}`).join(' · ');
      const skunkLine = skunkResult(winner, state.scores ? state.scores['you'] : 0, state.scores ? state.scores['cpu'] : 0);
      let skunkHtml = '';
      if (skunkLine) {
        skunkHtml = `<p class="skunk-banner ${skunkLine.kind}">${skunkLine.line}</p>`;
      }

      if (!state.replayRecorded) {
        state.replayRecorded = true;
        try {
          const repScores = {
            p1: state.scores ? (state.scores['you'] ?? state.scores['p1'] ?? 0) : 0,
            p2: state.scores ? (state.scores['cpu'] ?? state.scores['p2'] ?? 0) : 0
          };
          GameReplayManager.finishRecording(winner, repScores, skunkLine ? skunkLine.kind : null);
        } catch (err) {
          console.warn('[GameEngine] Error in finishRecording:', err);
        }
      }
      let recLine = '';
      if (!state.recorded) {
        state.recorded = true;
        const rec = recordGameResult(winner === 'p1' || winner === 'team1' ? 'you' : 'cpu');
        if (rec && typeof rec.wins === 'number' && typeof rec.losses === 'number') {
          recLine = `<p>Record for this setup — ${rec.wins}–${rec.losses}</p>`;
        }
        renderRecordBox();
      }

      const winnerName = pName(winner);
      const mySeatId = typeof mySeat === 'function' ? mySeat() : 'p1';
      const isWinnerMe = (winner === mySeatId || (winner === 'p1' && mySeatId === 'p1') || (winner === 'p2' && mySeatId === 'p2'));
      const title = isWinnerMe ? `Victory! ${winnerName} wins!` : `${winnerName} wins!`;

      if (isWinnerMe) {
        AchievementManager.incrementStat('totalWins', 1, { sfx });
        AchievementManager.unlock('first_blood', { sfx });
        if (skunkLine) {
          AchievementManager.incrementStat('totalSkunks', 1, { sfx });
          AchievementManager.unlock('skunk_master', { sfx });
          if (skunkLine.kind === 'double') {
            AchievementManager.incrementStat('totalDoubleSkunks', 1, { sfx });
            AchievementManager.unlock('double_skunk', { sfx });
          }
        }
        const curBrand = (settings.brand || context.brand || (typeof window !== 'undefined' ? (window.getAppBrand ? window.getAppBrand() : (window.currentBrand || 'crazy')) : 'crazy'));
        if (t === 481 || curBrand === 'clash') {
          AchievementManager.incrementStat('clashWins', 1, { sfx });
          AchievementManager.unlock('clash_conqueror', { sfx });
        }
        const isPints = (curBrand === 'pints' || settings.optYouDrink);
        if (isPints) {
          AchievementManager.incrementStat('pintsWins', 1, { sfx });
          if (!state.drinks || !state.drinks[mySeatId]) {
            AchievementManager.unlock('pub_legend', { sfx });
          }
        }
        const isAdult = (curBrand === 'adult' || settings.optAdult);
        if (isAdult) {
          AchievementManager.incrementStat('adultWins', 1, { sfx });
          AchievementManager.unlock('adult_first_strip', { sfx });
        }
        CelebrationFX.launchConfetti({ count: 110, duration: 3800, sfx });
        CelebrationFX.pulseScreen(skunkLine ? 'skunk' : 'gold', 1800);
      }
      // Record any final partial hand if game ended during pegging
      HandScoreTracker.recordFinalPartialHand(state, settings, winner, { pName });

      // Record series progression
      const seriesResult = SeriesTracker.recordGame({
        winner,
        winnerName,
        p1Score: state.scores ? (state.scores['you'] || state.scores['p1'] || 0) : 0,
        p2Score: state.scores ? (state.scores['cpu'] || state.scores['p2'] || 0) : 0,
        skunkKind: skunkLine ? skunkLine.kind : null,
        openingDealer: state.dealer
      });
      const seriesCardHtml = SeriesTracker.buildModalSummary({
        p1Name: pName('you'),
        p2Name: pName('cpu')
      });

      // Check if the current game is actually a tournament match
      const isTournament = Boolean(
        (settings && settings.isTournamentMatch) ||
        (context && (context.isTournament || context.isTournamentMatch)) ||
        (typeof window !== 'undefined' && window.settings && window.settings.isTournamentMatch)
      );

      // Record tournament match progression ONLY if a tournament match is actively flagged
      let tourneyCardHtml = '';
      if (isTournament) {
        const tourneyMatch = TournamentManager.getActiveMatch();
        if (tourneyMatch && tourneyMatch.status !== 'completed') {
          const tourneyResult = TournamentManager.recordGame({
            winner,
            winnerName,
            p1Score: state.scores ? (state.scores['you'] || state.scores['p1'] || 0) : 0,
            p2Score: state.scores ? (state.scores['cpu'] || state.scores['p2'] || 0) : 0,
            skunkKind: skunkLine ? skunkLine.kind : null
          });

          if (tourneyResult) {
            const match = tourneyResult.match;
            const p1 = TournamentManager.getPlayer(tourneyResult.state, match.player1Id);
            const p2 = TournamentManager.getPlayer(tourneyResult.state, match.player2Id);
            const p1Wins = (match.wins && match.player1Id && match.wins[match.player1Id]) || 0;
            const p2Wins = (match.wins && match.player2Id && match.wins[match.player2Id]) || 0;
            const isChamp = tourneyResult.isTournamentComplete;

            if (isChamp) {
              const champPlayer = tourneyResult.champion;
              const isHumanChamp = champPlayer && (champPlayer.type === 'human' || champPlayer.id === 'p1');
              if (isHumanChamp) {
                AchievementManager.recordTournamentWin(tourneyResult.state, { sfx });
              }
              CelebrationFX.launchConfetti({ count: 180, duration: 5000, sfx });
              CelebrationFX.pulseScreen('gold', 2500);
            }

            tourneyCardHtml = `
              <div class="tournament-match-result-card" style="margin: 14px 0; padding: 12px 16px; background: rgba(240,198,74,0.12); border: 2px solid #f0c64a; border-radius: 10px; text-align: center;">
                <div style="font-size: 0.8rem; font-weight: 800; color: #f0c64a; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">
                  🏆 ${tourneyResult.state.title} · ${match.roundLabel}
                </div>
                <div style="display: flex; justify-content: center; align-items: center; gap: 16px; margin: 6px 0;">
                  <div style="text-align: right; flex: 1;">
                    <div style="font-size: 0.95rem; font-weight: 700; color: ${p1Wins > p2Wins ? '#f0c64a' : '#f4ead4'};">${p1 ? p1.avatar : ''} ${p1 ? p1.name : 'P1'}</div>
                    <div style="font-size: 1.5rem; font-weight: 900; color: #f0c64a;">${p1Wins}</div>
                  </div>
                  <div style="font-size: 1.2rem; font-weight: 800; color: rgba(244,234,212,0.4);">-</div>
                  <div style="text-align: left; flex: 1;">
                    <div style="font-size: 0.95rem; font-weight: 700; color: ${p2Wins > p1Wins ? '#f0c64a' : '#f4ead4'};">${p2 ? p2.avatar : ''} ${p2 ? p2.name : 'P2'}</div>
                    <div style="font-size: 1.5rem; font-weight: 900; color: #f0c64a;">${p2Wins}</div>
                  </div>
                </div>
                ${isChamp ? `
                  <div style="margin-top: 8px; font-size: 1rem; font-weight: 900; color: #34d399; background: rgba(52,211,153,0.2); padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(52,211,153,0.4);">
                    👑 ${tourneyResult.champion ? tourneyResult.champion.name : 'Champion'} IS CROWNED THE TABLE CHAMPION!
                  </div>
                ` : (tourneyResult.matchFinished ? `
                  <div style="margin-top: 6px; font-size: 0.92rem; font-weight: 800; color: #34d399;">
                    ✔ ${tourneyResult.winner ? tourneyResult.winner.name : 'Winner'} wins the match and advances!
                  </div>
                ` : `
                  <div style="margin-top: 6px; font-size: 0.82rem; color: #d6d3d1;">
                    First to ${match.targetWins} wins advances in bracket
                  </div>
                `)}
              </div>
            `;
          }
        }
      }

      const nextGameNum = (seriesResult.state.gamesPlayed || 0) + 1;
      const rematchBtnText = seriesResult.isComplete ? '🏆 New Series' : `🔁 Rematch (Game ${nextGameNum})`;

      const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
      const winnerAvatarBadge = (isWinnerMe && avMgr && typeof avMgr.renderAvatarBadge === 'function')
        ? `<div style="display:flex;justify-content:center;margin-bottom:12px;">${avMgr.renderAvatarBadge(avMgr.getSelectedAvatarId(), { size: 68, showRing: true, ringColor: '#facc15' })}</div>`
        : '';

      const gameOverHtml = `${winnerAvatarBadge}<h2>${title}</h2>
         <p>Final score — ${scoreSummary}</p>
         <p>${settings.goal === 'low' ? 'Low ball: first to the target loses.' : 'First to the target wins.'}</p>
         ${skunkHtml}
         ${tourneyCardHtml || seriesCardHtml}
         ${recLine}
         <div class="game-over-hub">
            <div class="game-over-tools-card">
              <div class="game-over-tools-header">
                <span class="game-over-tools-title">MATCH ANALYSIS & REPLAY</span>
              </div>
              <div class="game-over-tools-grid">
                <button type="button" class="go-btn go-btn-summary" id="btnGameSummary">📊 Game Summary</button>
                <button type="button" class="go-btn go-btn-coach" id="btnCoachPostMortem">🎓 Coach's Post-Mortem</button>
                <button type="button" class="go-btn go-btn-replay" id="btnWatchReplay">🎬 Watch Replay</button>
              </div>
            </div>

            <div class="game-over-next-bar">
              ${isTournament ? `
                <button type="button" class="btn-gold go-btn go-btn-hero go-btn-tournament" id="btnTourneyBracket">🏆 Tournament Bracket</button>
              ` : `
                <button type="button" class="btn-gold go-btn go-btn-hero go-btn-rematch" id="btnRematchGame">${rematchBtnText}</button>
                <button type="button" class="btn-ghost go-btn go-btn-menu" id="btnGameOverNewGame">New game</button>
              `}
            </div>
          </div>`;

      GameEngine.lastGameOverHtml = gameOverHtml;
      if (typeof window !== 'undefined') {
        window._lastGameOverHtml = gameOverHtml;
        window.showGameWonModal = () => GameEngine.showGameWonModal();
      }

      showModal(gameOverHtml);
      if (typeof document !== 'undefined') {
        const modalEl = document.getElementById('modal');
        GameEngine.bindGameOverEvents(modalEl || document);
      }
    }
    return over;
  }

  /**
   * Re-opens the Game Won / Game Over modal if a finished match exists.
   * @returns {boolean} Whether the modal was displayed
   */
  static showGameWonModal() {
    const html = GameEngine.lastGameOverHtml || (typeof window !== 'undefined' ? window._lastGameOverHtml : null);
    if (!html) return false;
    const showModalFn = (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showModal === 'function')
      ? window.modalManager.showModal.bind(window.modalManager)
      : (typeof window !== 'undefined' && typeof window.showModal === 'function' ? window.showModal : null);
    if (showModalFn) {
      showModalFn(html, false);
      if (typeof document !== 'undefined') {
        const modalEl = document.getElementById('modal');
        GameEngine.bindGameOverEvents(modalEl || document);
      }
      return true;
    }
    return false;
  }

  /**
   * Binds click events for game over modal action buttons.
   * @param {Element|Document} [root=document]
   */
  static bindGameOverEvents(root = (typeof document !== 'undefined' ? document : null)) {
    if (!root || typeof root.querySelector !== 'function') return;
    const summaryBtn = root.querySelector('#btnGameSummary');
    if (summaryBtn && !summaryBtn._boundGameOver) {
      summaryBtn._boundGameOver = true;
      summaryBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.showGameSummaryModal === 'function') {
          window.showGameSummaryModal();
        }
      });
    }
    const coachBtn = root.querySelector('#btnCoachPostMortem');
    if (coachBtn && !coachBtn._boundGameOver) {
      coachBtn._boundGameOver = true;
      coachBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.showCoachPostMortemModal === 'function') {
          window.showCoachPostMortemModal();
        } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showCoachPostMortemModal === 'function') {
          window.modalManager.showCoachPostMortemModal();
        }
      });
    }
    const replayBtn = root.querySelector('#btnWatchReplay');
    if (replayBtn && !replayBtn._boundGameOver) {
      replayBtn._boundGameOver = true;
      replayBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.playCurrentGameReplay === 'function') {
          window.playCurrentGameReplay();
        } else if (typeof window !== 'undefined' && window.CrazyCribbage?.GameReplayManager?.playCurrentGameReplay) {
          window.CrazyCribbage.GameReplayManager.playCurrentGameReplay();
        } else if (typeof window !== 'undefined' && window.ReplayModal && typeof window.ReplayModal.open === 'function') {
          window.ReplayModal.open();
        }
      });
    }
    const tourneyBtn = root.querySelector('#btnTourneyBracket');
    if (tourneyBtn && !tourneyBtn._boundGameOver) {
      tourneyBtn._boundGameOver = true;
      tourneyBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && window.TournamentModal && typeof window.TournamentModal.open === 'function') {
          window.TournamentModal.open();
        }
      });
    }
    const rematchBtn = root.querySelector('#btnRematchGame');
    if (rematchBtn && !rematchBtn._boundGameOver) {
      rematchBtn._boundGameOver = true;
      rematchBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.playAgain === 'function') {
          window.playAgain();
        }
      });
    }
    const newGameBtn = root.querySelector('#btnGameOverNewGame');
    if (newGameBtn && !newGameBtn._boundGameOver) {
      newGameBtn._boundGameOver = true;
      newGameBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.backToMenu === 'function') {
          window.backToMenu();
        }
      });
    }
  }

  /**
   * Adds score delta, processes hazard triggers, toasts, notifications, and advances pegs.
   * @param {string} who
   * @param {number} n
   * @param {string} why
   * @param {boolean} [isPartnerBonus]
   * @param {boolean} [isSignAction]
   * @param {Object} [context]
   */
  static addScore(who, n, why, isPartnerBonus = false, isSignAction = false, context = {}) {
    if (!n) return;
    const state = context.state || (typeof window !== 'undefined' ? window.state : {});
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const scoreKey = context.scoreKey || (typeof window !== 'undefined' ? window.scoreKey : (w => w));
    const getActiveTracks = context.getActiveTracks || (typeof window !== 'undefined' ? window.getActiveTracks : () => ['p1', 'p2']);
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (w => w));
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const showScoreToast = context.showScoreToast || (typeof window !== 'undefined' ? window.showScoreToast : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
    const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : () => {});
    const fancyPegPhrase = context.fancyPegPhrase || (typeof window !== 'undefined' ? window.fancyPegPhrase : ((w, p) => `${w} +${p}`));
    const isBonusMessage = context.isBonusMessage || (typeof window !== 'undefined' ? window.isBonusMessage : () => false);
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const showStreetBanner = context.showStreetBanner || (typeof window !== 'undefined' ? window.showStreetBanner : () => {});
    const checkGameOver = context.checkGameOver || (typeof window !== 'undefined' ? window.checkGameOver : GameEngine.checkGameOver);
    const handleSignsLanding = context.handleSignsLanding || (typeof window !== 'undefined' ? window.handleSignsLanding : null);
    const getActivePintsOptionAtHole = context.getActivePintsOptionAtHole || (typeof window !== 'undefined' ? window.getActivePintsOptionAtHole : null);
    const showPintsBonusModal = context.showPintsBonusModal || (typeof window !== 'undefined' ? window.showPintsBonusModal : null);
    const getActiveAdultOptionAtHole = context.getActiveAdultOptionAtHole || (typeof window !== 'undefined' ? window.getActiveAdultOptionAtHole : null);
    const showAdultStripModal = context.showAdultStripModal || (typeof window !== 'undefined' ? window.showAdultStripModal : null);
    const showAdultTimerModal = context.showAdultTimerModal || (typeof window !== 'undefined' ? window.showAdultTimerModal : null);
    const pegTracker = context.pegTracker || (typeof window !== 'undefined' ? window.pegTracker : null);
    const isShiftingPartnership = context.isShiftingPartnership || (typeof window !== 'undefined' ? window.isShiftingPartnership : () => false);
    const getShiftingPartner = context.getShiftingPartner || (typeof window !== 'undefined' ? window.getShiftingPartner : () => null);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (w => w));

    const res = GameEngine.applyScoreDelta(who, n, why, {
      state,
      settings,
      scoreKey,
      activeTracks: getActiveTracks(),
      isSignAction,
      isPartnerBonus
    });

    if (res.noop) return;
    const key = res.key;
    const playerName = pName(who);

    if (res.stinkHoleBlocked) {
      log(`${playerName} stink hole — need exactly 1, ${n} not scored`);
      return;
    }

    if (res.hazard === 'jail') {
      const { used, left, freed, trapHole } = res.penaltyUsed;
      log(`🔒 JAILED: ${playerName} missed a pegging turn (${why || 'points'} for ${n} pts forfeited)! Turn ${used} of 3 used (${left} remaining).`);
      if (state.phase === 'play') {
        showScoreToast(who, 0, `Jailed! Forfeited +${n} (${used} of 3 turns used)`);
        fancyCall(`MISSED TURN (${used} OF 3 USED)`, 'jail', 2200);
      } else {
        fancyCall(`HAND FORFEITED (${used} OF 3 USED)`, 'jail', 2200);
      }
      if (freed) {
        log(`🔓 FREEDOM: ${playerName} served 3 of 3 missed turns and returns to the pegging track at hole ${trapHole}!`);
        fancyCall('RELEASED FROM JAIL!', 'jail-free', 2200);
      }
      renderScores(true);
      return;
    }

    if (res.hazard === 'bar') {
      const { used, left, freed, trapHole } = res.penaltyUsed;
      log(`🍻 AT THE BAR: ${playerName} missed a pegging turn (${why || 'points'} for ${n} pts forfeited)! You lost a turn, take a drink — Turn ${used} of 3 used (${left} remaining).`);
      showScoreToast(who, 0, `Lost a turn, take a drink (${used} of 3 turns used)`);
      fancyCall(`TAKE A DRINK (${used} OF 3 USED)`, 'bar', 2400);
      if (freed) {
        log(`🍺 TAB CLOSED: ${playerName} served 3 of 3 missed turns and leaves The Bar, returning to the pegging track at hole ${trapHole}!`);
        fancyCall('LEAVING THE BAR!', 'bar-free', 2200);
      }
      renderScores(true);
      return;
    }

    if (res.hazard === 'timeout') {
      const { used, left, freed, trapHole } = res.penaltyUsed;
      log(`⏳ TIMEOUT: ${playerName} missed a pegging turn (${why || 'points'} for ${n} pts forfeited)! You've been bad, you lose a turn — Turn ${used} of 3 used (${left} remaining).`);
      showScoreToast(who, 0, `Lost a turn in timeout (${used} of 3 turns used)`);
      fancyCall(`IN TIMEOUT (${used} OF 3 USED)`, 'timeout', 2400);
      if (freed) {
        log(`✨ TIMEOUT OVER: ${playerName} served 3 of 3 missed turns and leaves timeout, returning to the pegging track at hole ${trapHole}!`);
        fancyCall('OUT OF TIMEOUT!', 'timeout-free', 2200);
      }
      renderScores(true);
      return;
    }

    if (res.hazard === 'detour') {
      const { progress, cleared, targetHole } = res.detourProgress;
      if (!cleared) {
        log(`🚧 DETOUR: ${playerName} advanced to Detour Hole ${progress}/7 (+${n} ${why || 'pts'}).`);
        showScoreToast(who, n, `Detour Hole ${progress}/7`);
        renderScores(true);
        return;
      } else {
        log(`✨ DETOUR CLEARED: ${playerName} completed the 7-hole detour loop and returned to the track at Hole ${targetHole} (+${n} ${why || 'pts'})!`);
        fancyCall('DETOUR CLEARED!', 'detour-free', 2200);
        showScoreToast(who, n, `Cleared Detour ➔ Hole ${targetHole}`);
        renderScores(true);
        return;
      }
    }

    if (res.hazard === 'roundabout') {
      const { progress, cleared, targetHole } = res.roundAboutProgress;
      if (!cleared) {
        log(`🔄 ROUND ABOUT: ${playerName} advanced to Round About Hole ${progress}/7 (+${n} ${why || 'pts'}).`);
        showScoreToast(who, n, `Round About Hole ${progress}/7`);
        renderScores(true);
        return;
      } else {
        log(`✨ ROUND ABOUT CLEARED: ${playerName} completed the 7-hole rotary loop and returned to the track at Hole ${targetHole} (+${n} ${why || 'pts'})!`);
        fancyCall('ROUND ABOUT CLEARED!', 'roundabout-free', 2200);
        showScoreToast(who, n, `Cleared Round About ➔ Hole ${targetHole}`);
        renderScores(true);
        return;
      }
    }

    const ptsToAdd = res.scored;

    if (res.laneBlocked && res.laneBlocker) {
      const blockerName = pName(res.laneBlocker);
      log(`${playerName} cannot pass ${blockerName} in No Passing Lane (scored ${ptsToAdd} of ${n}, stopped at hole ${state.scores[key]})`);
      if (state.phase === 'play') {
        showScoreToast(who, ptsToAdd, `No Passing Lane (held behind ${blockerName})`);
      }

      const trapOpt = (typeof settings !== 'undefined' && settings && (settings.stopSignAnimation !== undefined ? settings.stopSignAnimation : (settings.stopAnimation !== undefined ? settings.stopAnimation : settings.speedTrapAnimation))) || (typeof window !== 'undefined' && window.settings && (window.settings.stopSignAnimation !== undefined ? window.settings.stopSignAnimation : (window.settings.stopAnimation !== undefined ? window.settings.stopAnimation : window.settings.speedTrapAnimation))) || 'yes';
      const isTrapAnimEnabled = trapOpt !== 'no' && trapOpt !== 'off' && trapOpt !== false;
      const showTrap = isTrapAnimEnabled ? (context.showSpeedTrapAnimation || (typeof window !== 'undefined' && window.showSpeedTrapAnimation) || (typeof window !== 'undefined' && window.SpeedTrapAnimationOverlay && window.SpeedTrapAnimationOverlay.show)) : null;
      if (typeof showTrap === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showTrap({
            playerName,
            who,
            reason: 'no_passing_lane',
            hole: state.scores[key],
            ptsScored: ptsToAdd,
            ptsLost: Math.max(0, n - ptsToAdd),
            blockerName,
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Speed Trap animation:', err);
        }
      }
    }

    if (res.stopSignHit) {
      log(`🛑 STOP SIGN: ${playerName} reached Stop Sign at hole ${res.stopSignHit.hole}! Counting stopped immediately (${ptsToAdd} of ${n} pts scored${res.stopSignHit.ptsLost > 0 ? `, ${res.stopSignHit.ptsLost} pts lost` : ''}).`);
      fancyCall(`STOP SIGN (HOLE ${res.stopSignHit.hole})!`, 'stop', 2400);
      showScoreToast(who, ptsToAdd, `Stop Sign at Hole ${res.stopSignHit.hole}! Counting stopped`);
      if (state.showMeter) {
        state.showMeter.total = ptsToAdd;
        state.showMeter.line = `Stopped at Hole ${res.stopSignHit.hole} (${ptsToAdd} pts)`;
        setShowMeter(state.showMeter.act, ptsToAdd, state.showMeter.line);
      }

      const trapOpt = (typeof settings !== 'undefined' && settings && (settings.stopSignAnimation !== undefined ? settings.stopSignAnimation : (settings.stopAnimation !== undefined ? settings.stopAnimation : settings.speedTrapAnimation))) || (typeof window !== 'undefined' && window.settings && (window.settings.stopSignAnimation !== undefined ? window.settings.stopSignAnimation : (window.settings.stopAnimation !== undefined ? window.settings.stopAnimation : window.settings.speedTrapAnimation))) || 'yes';
      const isTrapAnimEnabled = trapOpt !== 'no' && trapOpt !== 'off' && trapOpt !== false;
      const showTrap = isTrapAnimEnabled ? (context.showSpeedTrapAnimation || (typeof window !== 'undefined' && window.showSpeedTrapAnimation) || (typeof window !== 'undefined' && window.SpeedTrapAnimationOverlay && window.SpeedTrapAnimationOverlay.show)) : null;
      if (typeof showTrap === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showTrap({
            playerName,
            who,
            reason: 'stop_sign',
            hole: res.stopSignHit.hole,
            ptsScored: ptsToAdd,
            ptsLost: res.stopSignHit.ptsLost || 0,
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Speed Trap animation:', err);
        }
      }
    }

    if (res.jumpTriggered) {
      const { from, to, diff, isForward } = res.jumpTriggered;
      const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
      const jumpMessage = isForward ? `Jumping forward from ${from} to ${to}` : `Jumping backward from ${from} to ${to}`;
      log(`⚡ ${playerName}: ${jumpMessage} (${diffStr})!`);
      showScoreToast(who, diffStr, jumpMessage);
      fancyCall(jumpMessage, 'combo jump', 2400);

      const cannonOpt = (typeof settings !== 'undefined' && settings && (settings.jumpAnimation !== undefined ? settings.jumpAnimation : settings.cannonAnimation)) || (typeof window !== 'undefined' && window.settings && (window.settings.jumpAnimation !== undefined ? window.settings.jumpAnimation : window.settings.cannonAnimation)) || 'yes';
      const isCannonAnimEnabled = cannonOpt !== 'no' && cannonOpt !== 'off' && cannonOpt !== false;
      const showCannon = isCannonAnimEnabled ? (context.showCannonAnimation || (typeof window !== 'undefined' && window.showCannonAnimation) || (typeof window !== 'undefined' && window.CannonAnimationOverlay && window.CannonAnimationOverlay.show)) : null;
      if (typeof showCannon === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showCannon({
            playerName,
            who,
            fromHole: from,
            toHole: to,
            diff,
            isForward,
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display ACME Human Cannonball animation:', err);
        }
      }
    }

    if (res.newTrapLanded === 'jail') {
      log(`🚨 JAIL TRAP! ${playerName} landed on hole ${state.scores[key]} and was thrown into the Jail Cell! Must serve 3 missed pegging turns.`);
      if (state.phase === 'play') fancyCall('GO TO JAIL! (3 TURNS)', 'jail', 2400);

      const jailOpt = (typeof settings !== 'undefined' && settings && settings.jailAnimation) || (typeof window !== 'undefined' && window.settings && window.settings.jailAnimation) || 'yes';
      const isJailAnimEnabled = jailOpt !== 'no' && jailOpt !== 'off' && jailOpt !== false;
      const showJail = isJailAnimEnabled ? (context.showJailAnimation || (typeof window !== 'undefined' && window.showJailAnimation) || (typeof window !== 'undefined' && window.JailAnimationOverlay && window.JailAnimationOverlay.show)) : null;
      if (typeof showJail === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showJail({
            playerName,
            who,
            jailHole: state.scores[key],
            score: state.scores[key],
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Jailhouse Mugshot animation:', err);
        }
      }
    } else if (res.newTrapLanded === 'bar') {
      log(`🍻 THE BAR TRAP! ${playerName} landed on hole ${state.scores[key]} and stopped in at The Bar! Must serve 3 missed pegging turns.`);
      if (state.phase === 'play') fancyCall('HEADED TO THE BAR! (3 TURNS)', 'bar', 2400);

      const barOpt = (typeof settings !== 'undefined' && settings && settings.barAnimation) || (typeof window !== 'undefined' && window.settings && window.settings.barAnimation) || 'yes';
      const isBarAnimEnabled = barOpt !== 'no' && barOpt !== 'off' && barOpt !== false;
      const showBar = isBarAnimEnabled ? (context.showBarAnimation || (typeof window !== 'undefined' && window.showBarAnimation) || (typeof window !== 'undefined' && window.BarAnimationOverlay && window.BarAnimationOverlay.show)) : null;
      if (typeof showBar === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showBar({
            playerName,
            who,
            barHole: state.scores[key],
            score: state.scores[key],
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Saloon Bar Slide animation:', err);
        }
      }
    } else if (res.newTrapLanded === 'timeout') {
      log(`⏳ TIMEOUT TRAP! ${playerName} landed on hole ${state.scores[key]} and was sent to the Timeout Corner! Must serve 3 missed pegging turns.`);
      if (state.phase === 'play') fancyCall('SENT TO TIMEOUT! (3 TURNS)', 'timeout', 2400);
    } else if (res.newTrapLanded === 'detour') {
      log(`🚧 DETOUR: ${playerName} landed on hole ${state.scores[key]} — Take the scenic route on this Detour!`);
      showScoreToast(who, 0, "Take the scenic route on this Detour");
      fancyCall("Take the scenic route on this Detour", 'detour', 2600);

      const detourOpt = (typeof settings !== 'undefined' && settings && (settings.detourAnimation !== undefined ? settings.detourAnimation : settings.scenicRouteAnimation)) || (typeof window !== 'undefined' && window.settings && (window.settings.detourAnimation !== undefined ? window.settings.detourAnimation : window.settings.scenicRouteAnimation)) || 'yes';
      const isScenicEnabled = detourOpt !== 'no' && detourOpt !== 'off' && detourOpt !== false;
      const showScenic = isScenicEnabled ? (context.showScenicRouteAnimation || (typeof window !== 'undefined' && window.showScenicRouteAnimation) || (typeof window !== 'undefined' && window.ScenicRouteAnimationOverlay && window.ScenicRouteAnimationOverlay.show)) : null;
      if (typeof showScenic === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showScenic({
            type: 'detour',
            playerName,
            who,
            hole: state.scores[key],
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Detour animation:', err);
        }
      }
    } else if (res.newTrapLanded === 'roundabout') {
      log(`🔄 ROUND ABOUT: ${playerName} landed on hole ${state.scores[key]} — Have some fun on the Round About!`);
      showScoreToast(who, 0, "Have some fun on the Round About!");
      fancyCall("Have some fun on the Round About!", 'roundabout', 2600);

      const rotaryOpt = (typeof settings !== 'undefined' && settings && (settings.roundAboutAnimation !== undefined ? settings.roundAboutAnimation : settings.scenicRouteAnimation)) || (typeof window !== 'undefined' && window.settings && (window.settings.roundAboutAnimation !== undefined ? window.settings.roundAboutAnimation : window.settings.scenicRouteAnimation)) || 'yes';
      const isScenicEnabled = rotaryOpt !== 'no' && rotaryOpt !== 'off' && rotaryOpt !== false;
      const showScenic = isScenicEnabled ? (context.showScenicRouteAnimation || (typeof window !== 'undefined' && window.showScenicRouteAnimation) || (typeof window !== 'undefined' && window.ScenicRouteAnimationOverlay && window.ScenicRouteAnimationOverlay.show)) : null;
      if (typeof showScenic === 'function') {
        let playerCard = null;
        if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
        else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
        try {
          showScenic({
            type: 'roundabout',
            playerName,
            who,
            hole: state.scores[key],
            card: playerCard,
            settings
          });
        } catch (err) {
          console.error('Failed to display Round About animation:', err);
        }
      }
    }

    if (res.signBlockLanded && typeof handleSignsLanding === 'function') {
      handleSignsLanding(res.signBlockLanded, who);
    }

    if (res.diceLanded) {
      log(`🎲 DICE: ${playerName} landed on hole ${state.scores[key]} — Roll The Dice!`);
      showScoreToast(who, 0, "Roll The Dice!");
      fancyCall("ROLL THE DICE! 🎲", 'dice-roll', 2400);
      const openDice = context.openDiceRollModal || (typeof window !== 'undefined' ? window.openDiceRollModal : (typeof CC !== 'undefined' && CC?.ClashMiniGames?.openDiceRollModal ? CC.ClashMiniGames.openDiceRollModal : null));
      if (typeof openDice === 'function') {
        openDice(who, context);
      }
    }

    if (res.wheelLanded) {
      log(`🎡 SPINNING WHEEL: ${playerName} landed on hole ${state.scores[key]} — Spin The Wheel!`);
      showScoreToast(who, 0, "Spin The Wheel!");
      fancyCall("SPIN THE WHEEL! 🎡", 'wheel-spin', 2600);
      const openWheel = context.openWheelSpinModal || (typeof window !== 'undefined' ? window.openWheelSpinModal : (typeof CC !== 'undefined' && CC?.ClashMiniGames?.openWheelSpinModal ? CC.ClashMiniGames.openWheelSpinModal : null));
      if (typeof openWheel === 'function') {
        openWheel(who, context);
      }
    }

    if (typeof getActivePintsOptionAtHole === 'function') {
      const pintsHit = getActivePintsOptionAtHole(state.scores[key]);
      if (pintsHit) {
        const opt = pintsHit.item;
        const holeNum = state.scores[key];
        const desc = opt.description || opt.actionText || opt.name;
        log(`${opt.icon || '🍺'} ${opt.name.toUpperCase()}! ${playerName} landed on hole ${holeNum} — ${desc}!`);
        showScoreToast(who, 0, `${opt.name}! ${desc}`);
        fancyCall(`${opt.name.toUpperCase()}! ${opt.icon || '🍺'}`, 'pints-drink', 2400);
        if (typeof showPintsBonusModal === 'function') showPintsBonusModal(opt, playerName, holeNum);

        const barOpt = (typeof settings !== 'undefined' && settings && settings.barAnimation) || (typeof window !== 'undefined' && window.settings && window.settings.barAnimation) || 'yes';
        const isBarAnimEnabled = barOpt !== 'no' && barOpt !== 'off' && barOpt !== false;
        const showBar = isBarAnimEnabled ? (context.showBarAnimation || (typeof window !== 'undefined' && window.showBarAnimation) || (typeof window !== 'undefined' && window.BarAnimationOverlay && window.BarAnimationOverlay.show)) : null;
        if (typeof showBar === 'function') {
          let playerCard = null;
          if (state.lastPlayedCard) playerCard = state.lastPlayedCard;
          else if (state.hands && state.hands[who] && state.hands[who].length) playerCard = state.hands[who][0];
          try {
            showBar({
              playerName,
              who,
              drinkName: opt.name,
              score: state.scores[key],
              card: playerCard,
              isDrinkSpace: true,
              settings
            });
          } catch (err) {
            console.error('Failed to display Saloon Bar Slide animation for Drink space:', err);
          }
        }
      }
    }
    if (typeof getActiveAdultOptionAtHole === 'function') {
      const adultHit = getActiveAdultOptionAtHole(state.scores[key]);
      if (adultHit) {
        const opt = adultHit.item;
        if (opt.group === 'strip') {
          log(`${opt.icon} ${opt.name.toUpperCase()}! ${playerName} landed on hole ${state.scores[key]} — ${opt.actionText}!`);
          showScoreToast(who, 0, `${opt.name}! ${opt.actionText}`);
          fancyCall(`${opt.name.toUpperCase()}! ${opt.icon}`, 'strip-action', 2400);
          if (typeof showAdultStripModal === 'function') showAdultStripModal(opt, playerName, state.scores[key]);
        } else if (opt.group === 'adult' || opt.timed) {
          log(`🔥 ${opt.name.toUpperCase()}! ${playerName} landed on hole ${state.scores[key]} — Timed Challenge: ${opt.actionText}!`);
          showScoreToast(who, 0, `Challenge: ${opt.name}!`);
          fancyCall(`${opt.name.toUpperCase()}! 🔥`, 'adult-action', 2400);
          if (typeof showAdultTimerModal === 'function') showAdultTimerModal(opt, playerName, state.scores[key]);
        }
      }
    }

    if (pegTracker && typeof pegTracker.advance === 'function') {
      pegTracker.advance(key, state.scores[key]);
    }

    if (state.phase !== 'show') {
      if (!state.handPeg) state.handPeg = {};
      state.handPeg[key] = (state.handPeg[key] || 0) + ptsToAdd;
      if (key === 'p1' || key === 'team1') state.handPeg.you = state.handPeg[key];
      if (key === 'p2' || key === 'team2') state.handPeg.cpu = state.handPeg[key];
      if (!state.pegLog) state.pegLog = [];
      state.pegLog.push({
        who,
        n: ptsToAdd,
        why,
        count: state.count || 0,
        card: state.pile && state.pile.length ? state.pile[state.pile.length - 1] : state.starter
      });
    }

    if (sfx && typeof sfx.peg === 'function') sfx.peg();
    if (res.gameHoleReached && sfx && typeof sfx.gameHole === 'function') sfx.gameHole();
    log(`${playerName} peg ${ptsToAdd} — ${why}  (${state.scores[key]})`);

    if (typeof window !== 'undefined' && window._currentRenderer3D) {
      if (res.gameHoleReached) {
        window._currentRenderer3D.triggerActionCam({ type: 'winning_peg', who: key, hole: state.scores[key] });
      } else if (ptsToAdd >= 24) {
        window._currentRenderer3D.triggerActionCam({ type: 'big_hand', who: key, score: ptsToAdd });
      }
    }

    if (state.phase === 'play' && (!res.laneBlocked || ptsToAdd === n)) {
      showScoreToast(who, ptsToAdd, why);
      if (/go|heels|nobs/i.test(why) && !/fifteen|pair|run|thirty/i.test(why)) {
        fancyCall(fancyPegPhrase(why, ptsToAdd), 'combo', isBonusMessage(why) ? 2200 : 1400);
      }
      // Contextual AI Reactions
      const whyLower = (why || '').toLowerCase();
      if (who === 'cpu') {
        if (whyLower.includes('fifteen') && Math.random() < 0.28) {
          ReactionManager.triggerAiReaction('nice15', { sfx, aiName: playerName });
        } else if ((whyLower.includes('thirty') || whyLower.includes('31') || ptsToAdd >= 6) && Math.random() < 0.35) {
          ReactionManager.triggerAiReaction('boom', { sfx, aiName: playerName });
        }
      } else if (who === 'p1' || who === 'you') {
        if ((whyLower.includes('thirty') || whyLower.includes('31') || ptsToAdd >= 6) && Math.random() < 0.22) {
          ReactionManager.triggerAiReaction('noway', { sfx });
        }
      }
    }

    if (Array.isArray(res.passedMarks)) {
      for (const m of res.passedMarks) {
        setTimeout(() => showStreetBanner(who, m), 400);
      }
    }

    if (!isPartnerBonus && typeof isShiftingPartnership === 'function' && isShiftingPartnership()) {
      const partner = typeof getShiftingPartner === 'function' ? getShiftingPartner(who) : null;
      if (partner && partner !== who) {
        GameEngine.addScore(partner, n, `${why} (partner: ${seatName(who)})`, true, false, context);
      }
    }

    renderScores(true);
    checkGameOver(context);
    return res;
  }

  /**
   * Resets the active match to its initial state and triggers cut phase.
   * @param {Object} [context]
   * @returns {{ state: Object, gameHands: Array }}
   */
  static resetMatch(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const makeDeck = context.makeDeck || (typeof window !== 'undefined' ? window.makeDeck : () => []);
    if (typeof window !== 'undefined') {
      window.FULL_DECK = makeDeck(settings.decks);
    }
    if (typeof document !== 'undefined') {
      const menu = document.getElementById('menu');
      const game = document.getElementById('game');
      const logEl = document.getElementById('log');
      if (menu) menu.classList.add('hidden');
      if (game) game.classList.remove('hidden');
      if (logEl) logEl.innerHTML = '';
    }
    const pegTracker = context.pegTracker || (typeof window !== 'undefined' ? window.pegTracker : null);
    if (pegTracker && typeof pegTracker.reset === 'function') {
      pegTracker.reset();
    }
    HandScoreTracker.reset();
    const getSignConfigs = context.getSignConfigs || (typeof window !== 'undefined' ? window.getSignConfigs : null);
    const newState = GameEngine.createInitialMatchState(settings, { getSignConfigs });
    if (typeof context.setState === 'function') {
      context.setState(newState);
    }
    if (typeof window !== 'undefined') {
      window.state = newState;
    }

    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const buildBoard = context.buildBoard || (typeof window !== 'undefined' ? window.buildBoard : () => {});
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
    const beginCut = context.beginCut || (typeof window !== 'undefined' ? window.beginCut : () => {});

    hideModal();
    buildBoard();
    renderScores(false);
    beginCut();

    return { state: (typeof window !== 'undefined' && window.state) ? window.state : newState, gameHands: [] };
  }

  /**
   * Triggers a rematch or informs guest/host via network.
   * @param {Object} [context]
   */
  static playAgain(context = {}) {
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const resetMatch = context.resetMatch || (typeof window !== 'undefined' ? window.resetMatch : GameEngine.resetMatch);
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    // Check if previous series was already won/completed
    const seriesState = SeriesTracker.getSeriesState();
    if (seriesState.champion) {
      SeriesTracker.resetSeries();
    }

    if (isOnline() && settings.netRole === 'guest') {
      netSend({ type: 'intent', op: 'playAgain' });
      return;
    }

    resetMatch(context);

    // Fair alternating opening dealer for rematches
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (state) {
      const nextDealer = SeriesTracker.getNextOpeningDealer(state.dealer);
      state.dealer = nextDealer;
    }

    SeriesTracker.updateSeriesUi();
    ReactionManager.injectReactionUi();

    if (isOnline()) {
      netSend({ type: 'rematch' });
    }
    netPush();
  }

  /**
   * Confirms assignment of suit and rank for a wild card/joker.
   * @param {number} r - Rank
   * @param {string} su - Suit
   * @param {string} [seatId]
   * @param {Object} [context]
   */
  static confirmWildAs(r, su, seatId, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const c = state.pendingWild;
    if (!c) return;

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const hideWildPick = context.hideWildPick || (typeof window !== 'undefined' ? window.hideWildPick : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const RANK_NAME = context.RANK_NAME || (typeof window !== 'undefined' ? window.RANK_NAME : { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' });
    const SUIT_SYM = context.SUIT_SYM || (typeof window !== 'undefined' ? window.SUIT_SYM : { S: '♠', H: '♥', C: '♣', D: '♦' });
    const afterStarterAssigned = context.afterStarterAssigned || (typeof window !== 'undefined' ? window.afterStarterAssigned : GameEngine.afterStarterAssigned);
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (card) => Boolean(card && card.wild));
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const cpuDoPickYourCrib = context.cpuDoPickYourCrib || (typeof window !== 'undefined' ? window.cpuDoPickYourCrib : () => {});
    const beginDiscard = context.beginDiscard || (typeof window !== 'undefined' ? window.beginDiscard : GameEngine.beginDiscard);
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : null);

    if (isOnline() && settings.netRole === 'guest') {
      netSend({ type: 'intent', op: 'wild', cardId: c.id, r, s: su, phase: state.phase, seatId: seatId || mySeat() });
      hideWildPick(context);
      return;
    }
    c.r = r;
    c.s = su;
    c.playAs = { r: r, s: su };
    log('Joker is now ' + (RANK_NAME[r] || r) + (SUIT_SYM[su] || su));
    hideWildPick(context);
    if (state.phase === 'assign' && state.assignTarget === 'starter') {
      afterStarterAssigned(context);
      return;
    }
    if (state.phase === 'assign-p2') {
      const nextWild = (state.cpuHand || []).find(x => isWild(x) && !x.playAs);
      if (nextWild) {
        setStatus(pName('cpu') + ': choose the next joker.');
        render();
        if (typeof openWildPick === 'function') {
          openWildPick(nextWild, 'hand', context);
        }
        return;
      }
      state.phase = 'discard-p2';
      setStatus(pName('cpu') + ': select cards to throw to the crib.');
      render();
      return;
    }
    if (state.phase === 'assign') {
      const nextWild = (state.youHand || []).find(x => isWild(x) && !x.playAs);
      if (nextWild) {
        setStatus('Choose the next joker.');
        render();
        if (typeof openWildPick === 'function') {
          openWildPick(nextWild, 'hand', context);
        }
        return;
      }
      if (settings.optPickYourCrib && isSeatCpu(state.dealer)) {
        cpuDoPickYourCrib(context);
        return;
      }
      beginDiscard(context);
      return;
    }
    render();
  }

  /**
   * Enters the discard phase, computes target descriptions, and prompts the current player.
   * @param {Object} [context]
   */
  static beginDiscard(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const seatDiscardsToDeck = context.seatDiscardsToDeck || (typeof window !== 'undefined' ? window.seatDiscardsToDeck : () => false);
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const isDealerTeam = context.isDealerTeam || (typeof window !== 'undefined' ? window.isDealerTeam : () => false);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const updateCoachDisplay = context.updateCoachDisplay || (typeof window !== 'undefined' ? window.updateCoachDisplay : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});

    state.phase = 'discard';
    const count = (settings && settings.playerCount) || 2;
    const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
    if (isCanadianDoubles) {
      GameEngine.startCanadianDoublesFlow(context);
      return;
    }
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    let discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings.deal === 'super' ? 3 : 2));
    const curSeat = state.currentHumanDiscard || 'p1';
    const toDeck = seatDiscardsToDeck(curSeat);
    let targetDesc = 'the crib';

    if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      const isDealer = curSeat === state.dealer;
      discardN = isDealer ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
      targetDesc = isDealer ? 'your crib' : "the dealer's hand";
      const variantTitle = settings.optTwistedCribbage ? 'Twisted Cribbage' : 'Speedo Cribbage';
      setStatus(variantTitle + ': Select ' + discardN + ' card' + (discardN === 1 ? '' : 's') + ' to ' + (isDealer ? 'throw to ' + targetDesc : 'pass to ' + targetDesc) + '.');
    } else {
      if (toDeck) {
        targetDesc = 'the deck';
      } else if (isSoloVsPairs()) {
        targetDesc = (curSeat === 'p1') ? 'your crib' : 'the crib';
      } else {
        targetDesc = isDealerTeam(curSeat) ? 'your crib' : "opponent's crib";
      }
      setStatus('Select ' + discardN + ' card' + (discardN === 1 ? '' : 's') + ' to throw to ' + targetDesc + '.');
    }

    if (settings && settings.optFivesCribbage && !toDeck) {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0);
      const currHand = getSeatHand(curSeat) || [];
      const fivesInHand = currHand.filter(c => rankOf(c) === 5);
      if (fivesInHand.length > 0) {
        const reqFives = Math.min(fivesInHand.length, discardN);
        state.selected = fivesInHand.slice(0, reqFives).map(c => c.id);
        setStatus("Five's Cribbage: Discard any 5s in your hand to " + targetDesc + " (" + reqFives + " required).");
      }
    }

    if (settings && settings.optSevensAndEightsCribbage && !toDeck) {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0);
      const currHand = getSeatHand(curSeat) || [];
      const sevensAndEights = currHand.filter(c => {
        const r = rankOf(c);
        return r === 7 || r === 8;
      });
      if (sevensAndEights.length > 0) {
        const reqCount = Math.min(sevensAndEights.length, discardN);
        state.selected = sevensAndEights.slice(0, reqCount).map(c => c.id);
        setStatus("Sevens and Eights Cribbage: Discard any 7s and 8s in your hand to " + targetDesc + " (" + reqCount + " required).");
      }
    }
    updateCoachDisplay();
    render();
  }

  /**
   * Finalizes starter card selection, scores heels (Jack starter +2), and transitions to play or discard.
   * @param {Object} [context]
   */
  static afterStarterAssigned(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => c ? `${c.r}${c.s}` : '');
    const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (c) => c ? (c.r || 0) : 0);
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const addScore = context.addScore || (typeof window !== 'undefined' ? window.addScore : GameEngine.addScore);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : null);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const activeSeats = (typeof context.getActiveSeatIds === 'function')
      ? context.getActiveSeatIds()
      : (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function' ? window.getActiveSeatIds() : ['p1', 'p2']);
    const nextSeat = context.nextSeat
      || (typeof window !== 'undefined' && typeof window.nextSeat === 'function' ? window.nextSeat : (s) => GameEngine.advanceDealer(s, activeSeats));
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : GameEngine.scheduleOppPlay);
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (seat, cb) => cb && cb());

    const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
    const starterLabels = starters.map(c => cardLabel(c)).join(', ');
    log('Starter' + (starters.length > 1 ? 's are ' : ' is ') + starterLabels);

    const isSpadesCut = Boolean(settings && settings.optSpades && starters.some(isSpadeCard));
    state.spadesCut = isSpadesCut;

    let heelsPts = 0;
    let jackCount = 0;
    for (const st of starters) {
      if (rankOf(st) === 11) {
        heelsPts += 2;
        jackCount++;
      }
    }
    if (isSpadesCut && heelsPts > 0) {
      heelsPts *= 2;
    }
    const isHeels = heelsPts > 0;
    const heelsPhrase = jackCount > 1
      ? `${heelsPts} for his heels${isSpadesCut ? ' (spades x2)' : ''}`
      : `${heelsPts === 4 ? 'Four' : 'Two'} for his heels${isSpadesCut ? ' (spades x2)' : ''}`;
    if (isHeels) {
      fancyCall(heelsPhrase, '', 1200);
      addScore(state.dealer, heelsPts, `his heels (${jackCount > 1 ? `${jackCount} Jack starters` : 'Jack starter'})`, false, false, context);
      if (state.over) {
        render();
        netPush({ fancy: { text: `${heelsPhrase} (+${heelsPts})`, kind: '', ms: 1200 }, sfx: 'bell31' });
        return;
      }
    }

    if (isSpadesCut) {
      log('♠️ SPADES CUT! Pegging and heels points are doubled this hand!');
      fancyCall('♠️ Spades Cut! Double Pegging! ♠️', 'famous', 2200);
      const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
      if (sfx && typeof sfx.spades === 'function') {
        sfx.spades();
      }
    }

    if (settings && settings.optGuessStarter && state.starterGuess) {
      const { who: guessWho, rank: guessRank } = state.starterGuess;
      const RANK_NAME = context.RANK_NAME || (typeof window !== 'undefined' ? window.RANK_NAME : { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' });
      const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
      const whoName = pName(guessWho);
      const guessedRankName = RANK_NAME[guessRank] || String(guessRank);

      const isHit = starters.some(st => {
        const r = (st.playAs && st.playAs.r) || st.rank || st.r || rankOf(st) || 0;
        return Number(r) === Number(guessRank);
      });

      if (isHit) {
        log(`🎯 ${whoName} correctly guessed the starter card rank (${guessedRankName})! +3 points!`);
        fancyCall(`🎯 Starter Guessed! +3 pts`, 'famous', 2000);
        const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
        if (sfx && typeof sfx.bell === 'function') sfx.bell();
        else if (sfx && typeof sfx.snap === 'function') sfx.snap();
        addScore(guessWho, 3, `guess starter: ${guessedRankName}`, false, false, context);
        if (state.over) {
          state.starterGuess = null;
          render();
          netPush({ fancy: { text: `🎯 Starter Guessed! (+3)`, kind: 'famous', ms: 2000 } });
          return;
        }
      } else {
        log(`${whoName} guessed starter rank ${guessedRankName}, cut was ${starterLabels}.`);
      }
      state.starterGuess = null;
    }

    const proceed = () => {
      if (settings.optCutBeforeDiscard && !state.discardsComplete) {
        // Cut before discard: Proceed to discard now that starter is revealed
        if (settings.jokers && (getSeatHand('p1') || []).some(c => isWild(c) && !c.playAs)) {
          state.phase = 'assign';
          state.assignTarget = 'hand';
          setStatus('You were dealt a joker. Click it and choose any card.');
          render();
          const firstWild = (getSeatHand('p1') || []).find(c => isWild(c) && !c.playAs);
          if (typeof openWildPick === 'function' && firstWild) {
            openWildPick(firstWild, 'hand', context);
          }
        } else {
          startDiscardOrPickFlow(context);
        }
        netPush({
          fancy: isHeels ? { text: `${heelsPhrase} (+${heelsPts})`, kind: '', ms: 1200 } : null,
          sfx: isHeels ? 'bell31' : null
        });
        return;
      }

      state.phase = 'play';
      const cribEl = (typeof document !== 'undefined') ? document.getElementById('cribCards') : null;
      if (cribEl) {
        cribEl.classList.remove('counting');
        cribEl.classList.add('playing');
      }
      state.count = 0;
      state.pile = [];
      const leadSeat = nextSeat(state.dealer);
      state.turn = leadSeat;
      hideWait();
      render();
      netPush({
        fancy: isHeels ? { text: `${heelsPhrase} (+${heelsPts})`, kind: '', ms: 1200 } : null,
        sfx: isHeels ? 'bell31' : null
      });
      if (leadSeat && isSeatCpu(leadSeat)) {
        scheduleOppPlay(leadSeat, context);
      } else if (leadSeat && isSeatHuman(leadSeat)) {
        const announceDelay = isHeels ? 1200 : 0;
        setTimeout(() => {
          if (!state || state.over || state.phase !== 'play' || (state.turn !== leadSeat && state.passingTo !== leadSeat)) return;
          GameEngine.announcePlayTurn(leadSeat, context);
          if (!isOnline()) {
            passTo(leadSeat, () => {
              render();
            }, context);
          }
        }, announceDelay);
      }
    };

    if (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused) {
      state._resumeAfterSign = proceed;
      state._resumeAfterAdultTimer = proceed;
      state._resumeAfterPintsBonus = proceed;
      return;
    }
    proceed();
  }

  /**
   * Schedules or triggers the next turn during the pegging play phase.
   * @param {string} [seatId]
   * @param {Object} [context]
   */
  static scheduleOppPlay(seatId, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (state && (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused)) return;

    const seat = seatId || (state ? state.turn : null) || 'p2';
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const cpuSeatPlay = context.cpuSeatPlay || (typeof window !== 'undefined' ? window.cpuSeatPlay : () => {});
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const passTo = context.passTo || (typeof window !== 'undefined' ? window.passTo : (s, cb) => cb && cb());

    if (isOnline()) {
      if (isSeatCpu(seat)) {
        if (settings.netRole === 'host') {
          setTimeout(() => cpuSeatPlay(seat), 550);
        }
        return;
      }
      if (seat === mySeat()) {
        GameEngine.announcePlayTurn(seat, context);
        render();
        return;
      }
      setStatus(seatName(seat) + ' to play…');
      render();
      netPush();
      return;
    }
    if (isSeatHuman(seat)) {
      passTo(seat, () => {
        GameEngine.announcePlayTurn(seat, context);
        render();
      }, context);
      return;
    }
    setTimeout(() => cpuSeatPlay(seat), 550);
  }

  checkGameOver(context) {
    return GameEngine.checkGameOver(context);
  }

  addScore(who, n, why, isPartnerBonus, isSignAction, context) {
    return GameEngine.addScore(who, n, why, isPartnerBonus, isSignAction, context);
  }

  resetMatch(context) {
    return GameEngine.resetMatch(context);
  }

  playAgain(context) {
    return GameEngine.playAgain(context);
  }

  confirmWildAs(r, su, seatId, context) {
    return GameEngine.confirmWildAs(r, su, seatId, context);
  }

  beginDiscard(context) {
    return GameEngine.beginDiscard(context);
  }

  afterStarterAssigned(context) {
    return GameEngine.afterStarterAssigned(context);
  }

  /**
   * Determines if a player seat discards to the deck in 5+ player setups.
   * @param {string} seatId
   * @param {Object} [context]
   * @returns {boolean}
   */
  static seatDiscardsToDeck(seatId, context = {}) {
    const sId = (seatId === 'you') ? 'p1' : (seatId === 'cpu' ? 'p2' : seatId);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const count = (settings && settings.playerCount) || 2;
    if (count < 5) return false;
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || !state.dealer) return false;

    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => []);
    const active = getActiveSeatIds();
    const dIdx = active.indexOf(state.dealer);
    if (dIdx < 0) return false;
    const sIdx = active.indexOf(sId);
    if (sIdx < 0) return false;

    const posFromDealer = ((sIdx - dIdx + count - 1) % count) + 1;
    return posFromDealer > 4;
  }

  /**
   * Checks if a seat is on the dealer's team.
   * @param {string} seatId
   * @param {Object} [context]
   * @returns {boolean}
   */
  static isDealerTeam(seatId, context = {}) {
    let s = seatId;
    if (s === 'you') s = 'p1';
    if (s === 'cpu') s = 'p2';
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    if (isSoloVsPairs()) return s === 'p1';

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || !state.dealer) return false;
    let d = state.dealer;
    if (d === 'you') d = 'p1';
    if (d === 'cpu') d = 'p2';
    const isFixedTeams = context.isFixedTeams || (typeof window !== 'undefined' ? window.isFixedTeams : () => false);
    const getSeatTeam = context.getSeatTeam || (typeof window !== 'undefined' ? window.getSeatTeam : (seat) => seat);
    if (isFixedTeams()) {
      return getSeatTeam(d) === getSeatTeam(s);
    }
    return s === d;
  }

  /**
   * Handles discard selection click for opponent seats in pass-and-play.
   * @param {string} sId
   * @param {Object} c
   * @param {Object} [context]
   */
  static onOppSeatDiscardCard(sId, c, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.over || (state.phase !== 'discard' && state.phase !== 'draw-1' && state.phase !== 'draw-2' && state.phase !== 'draw-1-p2' && state.phase !== 'draw-2-p2')) return;
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    if (isOnline() || state.currentHumanDiscard !== sId) return;

    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const isDraw1 = state.phase === 'draw-1' || state.phase === 'draw-1-p2';
    const isDraw2 = state.phase === 'draw-2' || state.phase === 'draw-2-p2';
    if (isDraw1 || isDraw2) {
      const maxDraw = isDraw1 ? 5 : 3;
      state.selected = state.selected || [];
      const i = state.selected.indexOf(c.id);
      if (i >= 0) state.selected.splice(i, 1);
      else if (state.selected.length < maxDraw) state.selected.push(c.id);
      render();
      return;
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const count = parseInt(settings && settings.playerCount, 10) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
    let discardN = isCanadianDoubles ? 2 : (count >= 3 ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2)));
    if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      discardN = (sId === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }

    state.selected = state.selected || [];
    const i = state.selected.indexOf(c.id);
    if (i >= 0) state.selected.splice(i, 1);
    else if (state.selected.length < discardN) state.selected.push(c.id);
    render();
  }

  /**
   * Handles card interaction click for Player 2 (CPU / second human in HvH).
   * @param {Object} c
   * @param {Object} [context]
   */
  static onCpuCard(c, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);
    if (!isHvH() || state.over || state.frozen || state.phase === 'cut-starter' || state.phase === 'starter-ritual') return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const count = parseInt(settings && settings.playerCount, 10) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    let discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2));
    if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      discardN = ('p2' === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (card) => Boolean(card && card.wild));
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : () => {});
    const hideWildPick = context.hideWildPick || (typeof window !== 'undefined' ? window.hideWildPick : () => {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const VAL = context.VAL || (typeof window !== 'undefined' ? window.VAL : (r) => Math.min(10, r));
    const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (card) => card ? (card.r || 0) : 0);
    const playCard = context.playCard || (typeof window !== 'undefined' ? window.playCard : () => {});

    if (state.phase === 'assign-p2') {
      if (isWild(c) && !c.playAs) openWildPick(c, 'hand', context);
      return;
    }
    const isP2Draw1 = state.phase === 'draw-1-p2' || (!isOnline() && state.currentHumanDiscard === 'p2' && state.phase === 'draw-1');
    const isP2Draw2 = state.phase === 'draw-2-p2' || (!isOnline() && state.currentHumanDiscard === 'p2' && state.phase === 'draw-2');
    if (isP2Draw1 || isP2Draw2) {
      const maxDraw = isP2Draw1 ? 5 : 3;
      state.selected = state.selected || [];
      const i = state.selected.indexOf(c.id);
      if (i >= 0) state.selected.splice(i, 1);
      else if (state.selected.length < maxDraw) state.selected.push(c.id);
      const selLen = state.selected.length;
      const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
      if (selLen === 0) {
        setStatus(`Draw Cribbage (${isP2Draw1 ? '1st Draw' : '2nd Draw'}): Select 0–${maxDraw} cards to replace, then click 'Discard & Draw' (or 'Keep Hand').`);
      } else {
        setStatus(`Draw Cribbage: ${selLen} card${selLen === 1 ? '' : 's'} selected to replace. Click 'Discard & Draw (${selLen})' to draw replacements.`);
      }
      render();
      return;
    }
    if (state.phase === 'discard-p2' || (!isOnline() && state.currentHumanDiscard === 'p2' && state.phase === 'discard')) {
      state.selected = state.selected || [];
      const i = state.selected.indexOf(c.id);
      if (i >= 0) state.selected.splice(i, 1);
      else if (state.selected.length < discardN) state.selected.push(c.id);
      render();
      return;
    }
    if (state.phase === 'play' && (state.turn === 'cpu' || state.turn === 'p2') && !state.frozen && !state.pausedForSign && !state.paused && !state.pausedForAdultTimer && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      if (isWild(c) && !c.playAs) { openWildPick(c, 'hand', context); return; }
      if (state.count + VAL(rankOf(c)) > 31) return;
      hideWildPick(context);
      playCard('p2', c);
    }
  }

  /**
   * Handles card interaction click for Player 1 / primary user.
   * @param {Object} c
   * @param {Object} [context]
   */
  static onYouCard(c, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.over || state.phase === 'dealing') return;
    if (state.phase === 'cut' || state.phase === 'cut-starter' || state.phase === 'starter-ritual') return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const count = parseInt(settings && settings.playerCount, 10) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
    if (isCanadianDoubles && state.phase === 'discard') {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const curHand = getSeatHand(mySeat());
      if (curHand && curHand.length === 10) {
        const ModalCls = CanadianDoublesModal || (typeof CC !== 'undefined' ? CC.CanadianDoublesModal : (typeof window !== 'undefined' ? (window.CanadianDoublesModal || window.CrazyCribbage?.CanadianDoublesModal) : null));
        if (ModalCls && typeof ModalCls.open === 'function') {
          ModalCls.open(mySeat(), { ...context, onComplete: () => GameEngine.startCanadianDoublesFlow(context) });
          return;
        }
      }
    }
    let discardN = isCanadianDoubles ? 2 : (count >= 3 ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2)));
    if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      discardN = (state.dealer === 'p1') ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (card) => Boolean(card && card.wild));
    const openWildPick = context.openWildPick || (typeof window !== 'undefined' ? window.openWildPick : () => {});
    const hideWildPick = context.hideWildPick || (typeof window !== 'undefined' ? window.hideWildPick : () => {});
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const VAL = context.VAL || (typeof window !== 'undefined' ? window.VAL : (r) => Math.min(10, r));
    const rankOf = context.rankOf || (typeof window !== 'undefined' ? window.rankOf : (card) => card ? (card.r || 0) : 0);
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const playCard = context.playCard || (typeof window !== 'undefined' ? window.playCard : () => {});

    if (state.phase === 'assign') {
      if (state.assignTarget === 'starter') return;
      if (isWild(c) && !c.playAs) openWildPick(c, 'hand', context);
      return;
    }
    const me = mySeat();
    const activeHumanDiscard = !isOnline() ? (state.currentHumanDiscard || 'p1') : me;
    const canDiscard = (state.phase === 'discard' && (!isOnline() ? (activeHumanDiscard === 'p1') : (settings && settings.netRole === 'host' || isSeatHuman(me)))) ||
                       (state.phase === 'discard-p2' && isOnline() && me === 'p2');
    const isDraw1 = state.phase === 'draw-1' || (state.phase === 'draw-1-p2' && isOnline() && me === 'p2');
    const isDraw2 = state.phase === 'draw-2' || (state.phase === 'draw-2-p2' && isOnline() && me === 'p2');
    if (isDraw1 || isDraw2) {
      const maxDraw = isDraw1 ? 5 : 3;
      state.selected = state.selected || [];
      const i = state.selected.indexOf(c.id);
      if (i >= 0) state.selected.splice(i, 1);
      else if (state.selected.length < maxDraw) state.selected.push(c.id);
      const selLen = state.selected.length;
      const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
      if (selLen === 0) {
        setStatus(`Draw Cribbage (${isDraw1 ? '1st Draw' : '2nd Draw'}): Select 0–${maxDraw} cards to replace, then click 'Discard & Draw' (or 'Keep Hand').`);
      } else {
        setStatus(`Draw Cribbage: ${selLen} card${selLen === 1 ? '' : 's'} selected to replace. Click 'Discard & Draw (${selLen})' to draw replacements.`);
      }
      render();
      return;
    }
    if (canDiscard && !state.paused && !state.pausedForAdultTimer && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      state.selected = state.selected || [];
      const i = state.selected.indexOf(c.id);
      if (i >= 0) state.selected.splice(i, 1);
      else if (state.selected.length < discardN) state.selected.push(c.id);
      render();
    } else if (state.phase === 'play' && state.turn === me && !state.frozen && !state.pausedForSign && !state.paused && !state.pausedForAdultTimer && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      if (isWild(c) && !c.playAs) { openWildPick(c, 'hand', context); return; }
      if (state.count + VAL(rankOf(c)) > 31) return;
      hideWildPick(context);
      if (isOnline() && settings && settings.netRole === 'guest') {
        netSend({ type: 'intent', op: 'play', cardId: c.id, seatId: me });
        return;
      }
      playCard(me, c);
    }
  }

  seatDiscardsToDeck(seatId, context) {
    return GameEngine.seatDiscardsToDeck(seatId, context);
  }

  isDealerTeam(seatId, context) {
    return GameEngine.isDealerTeam(seatId, context);
  }

  onOppSeatDiscardCard(sId, c, context) {
    return GameEngine.onOppSeatDiscardCard(sId, c, context);
  }

  onCpuCard(c, context) {
    return GameEngine.onCpuCard(c, context);
  }

  onYouCard(c, context) {
    return GameEngine.onYouCard(c, context);
  }

  confirmDraw(context) {
    return GameEngine.confirmDraw(context);
  }

  startDrawCribbagePhase(phaseNum, context) {
    return GameEngine.startDrawCribbagePhase(phaseNum, context);
  }

  processNextDrawSeat(context) {
    return GameEngine.processNextDrawSeat(context);
  }

  cpuGuessStarterRank(poneSeat, state, settings, context) {
    return GameEngine.cpuGuessStarterRank(poneSeat, state, settings, context);
  }

  announcePlayTurn(seatId, context) {
    return GameEngine.announcePlayTurn(seatId, context);
  }
}
